/**
 * @description: Complete Super Admin Dashboard with Live Judging Features
 * @author: USER
 * @created: 06/09/2025
 * @version: 4.0.0 - Added Live Judging & Adjudication
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaUsers, FaCalendarPlus, FaTrophy, FaBell, FaChartBar, FaCalendarAlt, FaHandshake,
  FaCog, FaSignOutAlt, FaHome, FaUserShield, FaEdit,
  FaTimes, FaPlus, FaUpload, FaSave, FaTrash, FaEye,
  FaMedal, FaCrown, FaAward, FaGift, FaBars, FaChevronDown,
  FaSpinner, FaCheck, FaExclamationTriangle, FaInfoCircle,
  FaSync, FaFilter, FaDownload, FaToggleOn, FaToggleOff,
  FaVideo, FaPlay, FaStop, FaPause, FaRecordVinyl, FaGamepad,
  FaStar, FaUserTie, FaClipboardList, FaBroadcastTower,
  FaArchive, FaGraduationCap, FaMicrophone, FaSpellCheck, 
  FaBookOpen, FaPalette, FaFootballBall, FaCube, FaImages  // ✅ Added FaImages
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import { adminAPI, liveAPI, programsAPI, eventsAPI } from '../services/api';
import { useUser, useClerk } from '@clerk/clerk-react';
import AdvancedAnalytics3D from '../components/AdvancedAnalytics3D';
import { PaymentStatusBadge, ProgramPaymentBadge } from '../components/PaymentStatusBadge';

// Add this style tag in the component or in your global CSS
const mobileStyles = `
  @media (max-width: 1023px) {
    .dashboard-main-content {
      margin-left: 0 !important;
    }
  }

  /* Horizontal scroll indicator for tables */
  .overflow-x-auto {
    scrollbar-width: thin;
    scrollbar-color: rgba(156, 163, 175, 0.5) transparent;
  }

  .overflow-x-auto::-webkit-scrollbar {
    height: 8px;
  }

  .overflow-x-auto::-webkit-scrollbar-track {
    background: transparent;
  }

  .overflow-x-auto::-webkit-scrollbar-thumb {
    background-color: rgba(156, 163, 175, 0.5);
    border-radius: 10px;
  }

  .overflow-x-auto::-webkit-scrollbar-thumb:hover {
    background-color: rgba(156, 163, 175, 0.8);
  }

  /* Table cell min-width to prevent excessive shrinking */
  .participant-table th,
  .participant-table td {
    white-space: nowrap;
    min-width: 120px;
  }

  .participant-table th:first-child,
  .participant-table td:first-child {
    min-width: 180px;
  }
`;

const SuperAdminDashboard = () => {
  const { user, isLoaded } = useUser();
  const { signOut } = useClerk();
  const [activeTab, setActiveTab] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showArchivedEvents, setShowArchivedEvents] = useState(false);
  const [showAdvancedAnalytics, setShowAdvancedAnalytics] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  
  // Data states
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [winners, setWinners] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [dashboardStats, setDashboardStats] = useState({});
  
  // Gallery states (was missing — added to fix build errors)
  const [galleryItems, setGalleryItems] = useState([]);
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [editingGalleryItem, setEditingGalleryItem] = useState(null);
  const [galleryForm, setGalleryForm] = useState({
    mediaType: 'image', // 'image' or 'video'
    title: '',
    description: '',
    category: 'competitions',
    imageFiles: [],
    videoFile: null,
    videoUrl: ''
  });
  
  // Live judging states
  const [liveSessions, setLiveSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [scoreForm, setScoreForm] = useState({
    sessionId: '',
    userId: '',
    round: 1,
    technique: 0,
    composition: 0,
    creativity: 0,
    overall: 0,
    remarks: '',
    improvements: ''
  });
  
  // Modal states
  const [showEventModal, setShowEventModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [showWinnerModal, setShowWinnerModal] = useState(false);
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [showScoreModal, setShowScoreModal] = useState(false);

  const sidebarItems = [
    { id: 'overview', icon: FaHome, label: 'Overview' },
    { id: 'events', icon: FaCalendarPlus, label: 'Events' },
    { id: 'users', icon: FaUsers, label: 'Users' },
    { id: 'team', icon: FaUserTie, label: 'Team' },
    { id: 'programs', icon: FaGraduationCap, label: 'Programs' },
    { id: 'partners', icon: FaHandshake, label: 'Partners' },
    { id: 'gallery', icon: FaImages, label: 'Gallery' },
    { id: 'live-judging', icon: FaVideo, label: 'Live Judging' },
    { id: 'notifications', icon: FaBell, label: 'Notifications' },
    { id: 'analytics', icon: FaChartBar, label: 'Analytics' },
    { id: 'settings', icon: FaCog, label: 'Settings' }
  ];

  // Team management states
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [editingTeamMember, setEditingTeamMember] = useState(null);
  const [teamForm, setTeamForm] = useState({
    name: '',
    role: '',
    bio: '',
    email: '',
    phone: '',
    department: 'Other',
    expertise: [],
    socialMedia: {},
    isFeatured: false,
    isActive: true,
    displayOrder: 0,
    photoFile: null
  });

  // Fetch settings from backend
  const fetchSettings = async () => {
    try {
      const data = await adminAPI.getSettings();
      if (data.success) {
        setSettings(data.settings);
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
      toast.error('Failed to load settings');
    }
  };

  // Partners / Sponsors states & handlers
  const [sponsorsList, setSponsorsList] = useState([]);
  const [showSponsorModal, setShowSponsorModal] = useState(false);
  const [editingSponsor, setEditingSponsor] = useState(null);
  const [sponsorForm, setSponsorForm] = useState({
    name: '',
    website: '',
    tier: 'partner',
    type: 'organization',
    description: '',
    isFeatured: false,
    isActive: true,
    displayOrder: 0,
    logoFile: null
  });

  const fetchSponsorsAdmin = async () => {
    try {
      setLoading(true);
      const res = await adminAPI.getSponsors();
      if (res && res.sponsors) setSponsorsList(res.sponsors || []);
      else if (res && res.success && res.sponsors) setSponsorsList(res.sponsors || []);
      else setSponsorsList([]);
    } catch (error) {
      console.error('Failed to fetch sponsors:', error);
      toast.error('Failed to load partners');
    } finally {
      setLoading(false);
    }
  };

  const openCreateSponsorModal = () => {
    setEditingSponsor(null);
    setSponsorForm({ name: '', website: '', tier: 'partner', type: 'organization', description: '', isFeatured: false, isActive: true, displayOrder: 0, logoFile: null });
    setShowSponsorModal(true);
  };

  const openEditSponsorModal = (s) => {
    setEditingSponsor(s);
    setSponsorForm({ name: s.name || '', website: s.website || '', tier: s.tier || 'partner', type: s.type || 'organization', description: s.description || '', isFeatured: !!s.isFeatured, isActive: s.isActive !== false, displayOrder: s.displayOrder || 0, logoFile: null });
    setShowSponsorModal(true);
  };

  const handleSponsorFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setSponsorForm(prev => ({ ...prev, [name]: checked }));
    } else {
      setSponsorForm(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSponsorFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    setSponsorForm(prev => ({ ...prev, logoFile: file }));
  };

  const handleSponsorSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('name', sponsorForm.name || '');
      formData.append('website', sponsorForm.website || '');
      formData.append('tier', sponsorForm.tier || 'partner');
      formData.append('type', sponsorForm.type || 'organization');
  formData.append('description', sponsorForm.description || '');
      formData.append('isFeatured', sponsorForm.isFeatured ? 'true' : 'false');
      formData.append('isActive', sponsorForm.isActive ? 'true' : 'false');
      formData.append('displayOrder', sponsorForm.displayOrder || 0);
      if (sponsorForm.logoFile) formData.append('logo', sponsorForm.logoFile);

      let res;
      if (editingSponsor) {
        res = await adminAPI.updateSponsor(editingSponsor._id, formData);
      } else {
        res = await adminAPI.createSponsor(formData);
      }

      if (res && res.success) {
        toast.success(res.message || 'Saved successfully');
        setShowSponsorModal(false);
        fetchSponsorsAdmin();
      } else {
        throw new Error(res.message || 'Save failed');
      }
    } catch (error) {
      console.error('Sponsor save error:', error);
      toast.error(error.message || 'Failed to save partner');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSponsor = async (id) => {
    if (!window.confirm('Are you sure you want to delete this partner?')) return;
    try {
      setLoading(true);
      const res = await adminAPI.deleteSponsor(id);
      if (res && (res.success || res.status === 200)) {
        toast.success('Partner deleted');
        fetchSponsorsAdmin();
      } else {
        throw new Error(res.message || 'Delete failed');
      }
    } catch (error) {
      console.error('Delete sponsor error:', error);
      toast.error(error.message || 'Failed to delete partner');
    } finally {
      setLoading(false);
    }
  };

  // Team management handlers
  const fetchTeamMembers = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getTeamMembers();
      if (response.success) {
        setTeamMembers(response.teamMembers || []);
      } else {
        setTeamMembers([]);
      }
    } catch (error) {
      console.error('Failed to fetch team members:', error);
      toast.error('Failed to load team members');
    } finally {
      setLoading(false);
    }
  };

  const openCreateTeamModal = () => {
    setEditingTeamMember(null);
    setTeamForm({
      name: '', role: '', bio: '', email: '', phone: '', department: 'Other',
      expertise: [], socialMedia: {}, isFeatured: false, isActive: true, displayOrder: 0, photoFile: null
    });
    setShowTeamModal(true);
  };

  const openEditTeamModal = (member) => {
    setEditingTeamMember(member);
    setTeamForm({
      name: member.name || '',
      role: member.role || '',
      bio: member.bio || '',
      email: member.email || '',
      phone: member.phone || '',
      department: member.department || 'Other',
      expertise: member.expertise || [],
      socialMedia: member.socialMedia || {},
      isFeatured: !!member.isFeatured,
      isActive: member.isActive !== false,
      displayOrder: member.displayOrder || 0,
      photoFile: null
    });
    setShowTeamModal(true);
  };

  const handleTeamFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setTeamForm(prev => ({ ...prev, [name]: checked }));
    } else {
      setTeamForm(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleTeamFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    setTeamForm(prev => ({ ...prev, photoFile: file }));
  };

  const handleTeamSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('name', teamForm.name || '');
      formData.append('role', teamForm.role || '');
      formData.append('bio', teamForm.bio || '');
      formData.append('email', teamForm.email || '');
      formData.append('phone', teamForm.phone || '');
      formData.append('department', teamForm.department || 'Other');
      formData.append('displayOrder', teamForm.displayOrder || 0);
      formData.append('isFeatured', teamForm.isFeatured ? 'true' : 'false');
      formData.append('isActive', teamForm.isActive ? 'true' : 'false');
      formData.append('expertise', JSON.stringify(Array.isArray(teamForm.expertise) ? teamForm.expertise : (teamForm.expertise || '').split(',').map(s=>s.trim()).filter(Boolean)));
      formData.append('socialMedia', JSON.stringify(teamForm.socialMedia || {}));
      if (teamForm.photoFile) formData.append('photo', teamForm.photoFile);

      let res;
      if (editingTeamMember) {
        res = await adminAPI.updateTeamMember(editingTeamMember._id, formData);
      } else {
        res = await adminAPI.createTeamMember(formData);
      }

      if (res.success) {
        toast.success(res.message || 'Saved successfully');
        setShowTeamModal(false);
        fetchTeamMembers();
      } else {
        throw new Error(res.message || 'Save failed');
      }
    } catch (error) {
      console.error('Team save error:', error);
      toast.error(error.message || 'Failed to save team member');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTeamMember = async (id) => {
    if (!window.confirm('Are you sure you want to delete this team member?')) return;
    try {
      setLoading(true);
      const res = await adminAPI.deleteTeamMember(id);
      if (res && (res.success || res.status === 200)) {
        toast.success('Team member deleted');
        fetchTeamMembers();
      } else {
        throw new Error(res.message || 'Delete failed');
      }
    } catch (error) {
      console.error('Delete team member error:', error);
      toast.error(error.message || 'Failed to delete team member');
    } finally {
      setLoading(false);
    }
  };

  // Handle setting toggle with backend persistence
  const handleSettingToggle = async (settingKey) => {
    setSettingsLoading(true);
    try {
      const newSettings = {
        ...settings,
        [settingKey]: !settings[settingKey]
      };
      
      // Save to backend
      const response = await adminAPI.updateSettings(newSettings);
      
      if (response.success) {
        // Update local state
        setSettings(newSettings);
        
        const action = newSettings[settingKey] ? 'enabled' : 'disabled';
        const friendlyName = settingKey.replace(/([A-Z])/g, ' $1').toLowerCase();
        toast.success(`${friendlyName} ${action}`);
      } else {
        throw new Error(response.message || 'Failed to update setting');
      }
    } catch (error) {
      console.error('Setting toggle error:', error);
      toast.error('Failed to update setting');
    } finally {
      setSettingsLoading(false);
    }
  };
  // Utility function to safely format dates
  const formatDate = (dateInput) => {
    if (!dateInput) return 'Date not set';
    
    try {
      let date;
      
      // Handle different date formats
      if (typeof dateInput === 'string') {
        // Try parsing ISO string first
        date = new Date(dateInput);
        
        // If invalid, try parsing as timestamp
        if (isNaN(date.getTime())) {
          const timestamp = parseInt(dateInput);
          if (!isNaN(timestamp)) {
            date = new Date(timestamp);
          }
        }
      } else if (typeof dateInput === 'number') {
        // Handle timestamp
        date = new Date(dateInput);
      } else if (dateInput instanceof Date) {
        date = dateInput;
      } else {
        return 'Invalid date format';
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid date';
      }
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return 'Date error';
    }
  };

  // Utility function for relative time
  const formatRelativeDate = (dateInput) => {
    try {
      const date = new Date(dateInput);
      if (isNaN(date.getTime())) return 'Unknown date';
      
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
      if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
      return `${Math.floor(diffDays / 365)} years ago`;
    } catch (error) {
      return 'Unknown date';
    }
  };
  
  // Form states
  const [eventForm, setEventForm] = useState({
    title: '',
    description: '',
    date: '',
    endDate: '',
    time: '',
    endTime: '',
    timezone: 'EAT', // East Africa Time
    location: '',
    type: 'tournament',
    levels: [], // Multiple selection
    maxParticipants: 50,
    maxInstitutions: 10, // For schools/teams
    prize: '',
    registrationFee: '', // Text field for flexibility
    registrationCurrency: 'KES', // Currency selection
    paymentRequired: false, // Toggle
    featured: false,
    thumbnailFile: null
  });
  
  const [notificationForm, setNotificationForm] = useState({
    title: '',
    message: '',
    type: 'info',
    targetUsers: 'all'
  });

  const [winnerForm, setWinnerForm] = useState({
    eventId: '',
    winners: [
      { position: '1st', userId: '', prize: '' },
      { position: '2nd', userId: '', prize: '' },
      { position: '3rd', userId: '', prize: '' }
    ]
  });

  const [sessionForm, setSessionForm] = useState({
    title: '',
    eventId: '',
    category: 'cultural',
    maxParticipants: 20,
    roundsCount: 3,
    timePerRound: 15,
    description: '',
    streamUrl: '',
    isLive: false
  });

  const [programs, setPrograms] = useState([]);
  const [showArchivedPrograms, setShowArchivedPrograms] = useState(false);
  const [showProgramModal, setShowProgramModal] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);
  const [programForm, setProgramForm] = useState({
    title: '',
    description: '',
    icon: 'FaUsers',
    features: [''],
    duration: '',
    badge: '',
    badgeColor: 'bg-green-100 text-green-700 border-green-200',
    category: 'core',
    targetAge: { min: 5, max: 18 },
    capacity: 30,
    price: { amount: 0, currency: 'KES' },
    requirements: [''],
    objectives: [''],
    schedule: {
      frequency: 'Weekly',
      duration: '2 hours',
      days: []
    },
    instructor: {
      name: '',
      bio: '',
      qualifications: []
    },
    materials: [],
    enrollmentStatus: 'open',
    isFeatured: false,
    isActive: true,
    thumbnailFile: null
  });

  const fetchPrograms = async () => {
    try {
      const params = {
        archived: showArchivedPrograms ? 'true' : 'false'
      };
      
      const data = await programsAPI.getAll(params);
      
      if (data.success) {
        setPrograms(data.programs || []);
      } else if (Array.isArray(data)) {
        setPrograms(data);
      }
    } catch (error) {
      toast.error('Failed to load programs');
    }
  };

  const handleCreateProgram = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const formData = new FormData();
      
      // Append all form fields
      Object.keys(programForm).forEach(key => {
        if (key === 'thumbnailFile' && programForm[key]) {
          formData.append('thumbnail', programForm[key]);
        } else if (key !== 'thumbnailFile' && programForm[key] !== null) {
          if (typeof programForm[key] === 'object') {
            formData.append(key, JSON.stringify(programForm[key]));
          } else {
            formData.append(key, programForm[key]);
          }
        }
      });

      let data;
      if (editingProgram) {
        data = await programsAPI.update(editingProgram._id, formData);
        setPrograms(programs.map(p => p._id === editingProgram._id ? data.program : p));
        toast.success('Program updated successfully!');
      } else {
        data = await programsAPI.create(formData);
        setPrograms([...programs, data.program]);
        toast.success('Program created successfully!');
      }
      
      setShowProgramModal(false);
      setEditingProgram(null);
      resetProgramForm();
    } catch (error) {
      toast.error(error.message || 'Failed to save program');
    } finally {
      setLoading(false);
    }
  };

  const handleEditProgram = (program) => {
    setEditingProgram(program);
    setProgramForm({
      title: program.title || '',
      description: program.description || '',
      icon: program.icon || 'FaUsers',
      features: program.features || [''],
      duration: program.duration || '',
      badge: program.badge || '',
      badgeColor: program.badgeColor || 'bg-green-100 text-green-700 border-green-200',
      category: program.category || 'core',
      targetAge: program.targetAge || { min: 5, max: 18 },
      capacity: program.capacity || 30,
      price: program.price || { amount: 0, currency: 'KES' },
      requirements: program.requirements || [''],
      objectives: program.objectives || [''],
      schedule: program.schedule || {
        frequency: 'Weekly',
        duration: '2 hours',
        days: []
      },
      instructor: program.instructor || {
        name: '',
        bio: '',
        qualifications: []
      },
      materials: program.materials || [],
      enrollmentStatus: program.enrollmentStatus || 'open',
      isFeatured: program.isFeatured || false,
      isActive: program.isActive !== false,
      thumbnailFile: null
    });
    setShowProgramModal(true);
  };


  const handleUnregisterParticipant = async (eventId, participantId) => {
    try {
      setLoading(true);

      // Use the new admin unregister endpoint
      const response = await eventsAPI.adminUnregisterParticipant(eventId, participantId);

      const ok = response && (
        response.success === true ||
        response.status === 200 ||
        response.ok === true ||
        (typeof response === 'object' && !response.error && (response.message || Object.keys(response).length > 0))
      );

      if (ok) {
        toast.success(response.message || 'Participant removed successfully');

        // Update local state immediately
        setEvents(events.map(event => {
          if (event._id === eventId) {
            return {
              ...event,
              participants: event.participants.filter(p => p._id !== participantId)
            };
          }
          return event;
        }));

        // Refresh events data from backend
        try {
          const eventsParams = {
            archived: showArchivedEvents ? 'true' : 'false'
          };
          const eventsData = await adminAPI.getEvents(eventsParams);
          setEvents(eventsData.events || []);
        } catch (refreshError) {
          console.error('Error refreshing events:', refreshError);
          // Local state is already updated, so this is not critical
        }
      } else {
        // Response did not indicate success - show message if available
        console.error('Unexpected unregister response:', response);
        toast.error((response && (response.message || response.error)) || 'Failed to remove participant');
      }
    } catch (error) {
      console.error('Unregister participant error:', error);
      toast.error(error.response?.data?.message || 'Failed to remove participant');
    } finally {
      setLoading(false);
    }
  };

  const handleUnenrollFromProgram = async (programId, userId) => {
    try {
      setLoading(true);

      // Use the admin unenroll endpoint
      const response = await programsAPI.adminUnenrollUser(programId, userId);

      if (response.success) {
        toast.success('User removed from program successfully');

        // Update local state immediately
        setPrograms(programs.map(program => {
          if (program._id === programId) {
            return {
              ...program,
              enrolledUsers: program.enrolledUsers.filter(e => e.userId?._id !== userId),
              currentEnrollment: Math.max(0, (program.currentEnrollment || 0) - 1)
            };
          }
          return program;
        }));

        // Refresh programs data from backend
        try {
          const programsData = await adminAPI.getPrograms({
            archived: showArchivedPrograms ? 'true' : 'false'
          });
          setPrograms(programsData.programs || []);
        } catch (refreshError) {
          console.error('Error refreshing programs:', refreshError);
          // Local state is already updated, so this is not critical
        }
      }
    } catch (error) {
      console.error('Unenroll user error:', error);
      toast.error(error.response?.data?.message || 'Failed to remove user from program');
    } finally {
      setLoading(false);
    }
  };


  const handleViewProgram = (program) => {
    const programDetails = `Program Details:

  Title: ${program.title}
  Category: ${program.category.charAt(0).toUpperCase() + program.category.slice(1)}
  Duration: ${program.duration}
  Badge: ${program.badge}

  Description: ${program.description}

  Enrollment: ${program.currentEnrollment || 0}/${program.capacity} students
  Target Age: ${program.targetAge?.min || 5}-${program.targetAge?.max || 18} years
  Price: ${program.price?.currency || 'KES'} ${program.price?.amount?.toLocaleString() || '0'}

  Features:
  ${program.features?.map(f => `• ${f}`).join('\n') || 'No features listed'}

  Status: ${program.isArchived ? 'Archived' : 'Active'}
  Featured: ${program.isFeatured ? 'Yes' : 'No'}
  Enrollment Status: ${program.enrollmentStatus || 'Open'}

  Created: ${new Date(program.createdAt).toLocaleDateString()}
  Last Updated: ${new Date(program.updatedAt).toLocaleDateString()}`;
    
    alert(programDetails);
  };

  const handleArchiveProgram = async (programId) => {
    const program = programs.find(p => p._id === programId);
    const action = program?.isArchived ? 'unarchive' : 'archive';
    
    if (!window.confirm(`Are you sure you want to ${action} this program?`)) return;
    
    try {
      const data = await programsAPI.archive(programId);
      
      if (data.success) {
        setPrograms(programs.map(p => 
          p._id === programId ? { ...p, isArchived: !p.isArchived } : p
        ));
        toast.success(`Program ${action}d successfully!`);
      } else {
        throw new Error(data.message || `Failed to ${action} program`);
      }
    } catch (error) {
      toast.error(error.message || `Failed to ${action} program`);
    }
  };

  const handleDeleteProgram = async (programId) => {
    if (!window.confirm('Are you sure you want to permanently delete this program? This action cannot be undone.')) return;
    
    try {
      const data = await programsAPI.delete(programId);
      
      if (data.success) {
        setPrograms(programs.filter(p => p._id !== programId));
        toast.success('Program deleted successfully!');
      } else {
        throw new Error(data.message || 'Failed to delete program');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to delete program');
    }
  };

  // Add archive handler for events
  const handleArchiveEvent = async (eventId) => {
    const event = events.find(e => e._id === eventId);
    const action = event?.isArchived ? 'unarchive' : 'archive';
    
    if (!window.confirm(`Are you sure you want to ${action} this event?`)) return;
    
    try {
      const data = await adminAPI.archiveEvent(eventId);
      
      if (data.success) {
        setEvents(events.map(e => 
          e._id === eventId ? { ...e, isArchived: !e.isArchived } : e
        ));
        toast.success(`Event ${action}d successfully!`);
      }
    } catch (error) {
      toast.error(error.message || `Failed to ${action} event`);
    }
  };

  const resetProgramForm = () => {
    setProgramForm({
      title: '',
      description: '',
      icon: 'FaUsers',
      features: [''],
      duration: '',
      badge: '',
      badgeColor: 'bg-green-100 text-green-700 border-green-200',
      category: 'core',
      targetAge: { min: 5, max: 18 },
      capacity: 30,
      price: { amount: 0, currency: 'KES' },
      requirements: [''],
      objectives: [''],
      schedule: {
        frequency: 'Weekly',
        duration: '2 hours',
        days: []
      },
      instructor: {
        name: '',
        bio: '',
        qualifications: []
      },
      materials: [],
      enrollmentStatus: 'open',
      isFeatured: false,
      isActive: true,
      thumbnailFile: null
    });
  };

  const addArrayField = (field) => {
    setProgramForm({
      ...programForm,
      [field]: [...programForm[field], '']
    });
  };

  const removeArrayField = (field, index) => {
    const newArray = programForm[field].filter((_, i) => i !== index);
    setProgramForm({
      ...programForm,
      [field]: newArray.length === 0 ? [''] : newArray
    });
  };

  const updateArrayField = (field, index, value) => {
    const newArray = [...programForm[field]];
    newArray[index] = value;
    setProgramForm({
      ...programForm,
      [field]: newArray
    });
  };

  // Settings states
  const [settings, setSettings] = useState({
    emailNotifications: true,
    autoApproval: false,
    maintenanceMode: false,
    registrationOpen: true
  });

  // Check if user is super admin
  const SUPERADMIN_EMAILS = ['info@africandebatechampions.org', 'africandebatechampions@gmail.com'];
  const isSuperAdmin = SUPERADMIN_EMAILS.includes(user?.primaryEmailAddress?.emailAddress);

  // Handle window resize for desktop sidebar behavior
  useEffect(() => {
  const handleResize = () => {
    if (window.innerWidth >= 1024) {
      setSidebarOpen(true); // Always open on desktop
    } else {
      setSidebarOpen(false); // Always closed on mobile by default
    }
  };

  // Set initial state based on screen size
  handleResize();
  
  window.addEventListener('resize', handleResize);
  return () => window.removeEventListener('resize', handleResize);
}, []);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchDashboardData();
      fetchSettings();
    }
  }, [isSuperAdmin]);

  // Add this useEffect after your existing useEffects
  useEffect(() => {
    if (activeTab === 'programs') {
      fetchPrograms();
    }
  }, [showArchivedPrograms]);

  useEffect(() => {
    if (activeTab === 'team') {
      fetchTeamMembers();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'partners') {
      fetchSponsorsAdmin();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'events') {
      fetchDashboardData(false);
    }
  }, [showArchivedEvents]);

  const fetchDashboardData = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    setRefreshing(true);
    
    try {
      const eventsParams = {
        archived: showArchivedEvents ? 'true' : 'false'
      };
      
      const programsParams = {
        archived: showArchivedPrograms ? 'true' : 'false'
      };
      
      const [eventsData, usersData, notificationsData, liveSessionsData, programsData, galleryData] = await Promise.all([
        adminAPI.getEvents(eventsParams),
        adminAPI.getUsers(),
        adminAPI.getNotifications(),
        liveAPI.getLiveSessions().catch(() => ({ sessions: [] })),
        adminAPI.getPrograms(programsParams).catch(() => ({ programs: [] })),
        fetchGallery().catch(() => [])
      ]);
        
      setEvents(eventsData.events || []);
      setUsers(usersData.users || []);
      setNotifications(notificationsData.notifications || []);
      setLiveSessions(liveSessionsData.sessions || []);
      
      // Handle programs data with different response structures
      if (programsData.success) {
        setPrograms(programsData.programs || []);
      } else if (Array.isArray(programsData)) {
        setPrograms(programsData);
      } else {
        setPrograms([]);
      }
  
      // ✅ Fixed: Now galleryData is properly defined from Promise.all
      setGalleryItems(Array.isArray(galleryData) ? galleryData : galleryData.gallery || []);
      
      // Extract winners from events
      const allWinners = [];
      eventsData.events?.forEach(event => {
        if (event.winners && event.winners.length > 0) {
          event.winners.forEach(winner => {
            allWinners.push({
              ...winner,
              eventTitle: event.title,
              eventId: event._id
            });
          });
        }
      });
      setWinners(allWinners);
      
    } catch (error) {
      toast.error(error.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleViewUser = (user) => {
    alert(`User Details:\n\nName: ${user.name || user.teamName || user.schoolName}\nEmail: ${user.email}\nType: ${user.registrationType}\nStatus: ${user.status}\nJoined: ${new Date(user.createdAt).toLocaleDateString()}\n\n${user.phone ? `Phone: ${user.phone}\n` : ''}${user.location ? `Location: ${user.location}` : ''}`);
  };

  const handleRefresh = () => {
    fetchDashboardData(false);
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Validation
    if (eventForm.levels.length === 0) {
      toast.error('Please select at least one eligible level');
      setLoading(false);
      return;
    }
    
    try {
      const formData = new FormData();
      
      // Append all form fields including new ones
      Object.keys(eventForm).forEach(key => {
        if (key === 'thumbnailFile') {
          if (eventForm[key]) {
            formData.append('thumbnail', eventForm[key]);
          }
        } else if (key === 'levels') {
          // Send as JSON array
          formData.append('levels', JSON.stringify(eventForm[key]));
        } else if (eventForm[key] !== null && eventForm[key] !== '') {
          formData.append(key, eventForm[key]);
        }
      });
  
      let data;
      if (editingEvent) {
        // Update existing event
        data = await adminAPI.updateEvent(editingEvent._id, formData);
        setEvents(events.map(e => e._id === editingEvent._id ? data.event : e));
        toast.success('Event updated successfully!');
      } else {
        // Create new event
        data = await adminAPI.createEvent(formData);
        setEvents([...events, data.event]);
        toast.success('Event created successfully!');
      }
      
      setShowEventModal(false);
      setEditingEvent(null);
      resetEventForm();
    } catch (error) {
      console.error('Event creation error:', error);
      toast.error(error.message || 'Failed to save event');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadGalleryImage = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Handle video upload
      if (galleryForm.mediaType === 'video') {
        if (!galleryForm.videoFile && !galleryForm.videoUrl) {
          toast.error('Please select a video file or provide a video URL');
          return;
        }

        const data = await uploadGalleryVideo(
          galleryForm.videoFile,
          galleryForm.videoUrl,
          galleryForm.title,
          galleryForm.description,
          galleryForm.category
        );

        if (data.success) {
          setGalleryItems([data.item, ...galleryItems]);
          setShowGalleryModal(false);
          setGalleryForm({
            title: '',
            description: '',
            category: 'competitions',
            mediaType: 'image',
            imageFiles: [],
            videoFile: null,
            videoUrl: ''
          });
          toast.success('Video uploaded successfully!');
        }
        return;
      }

      // Handle image upload
      if (!galleryForm.imageFiles || galleryForm.imageFiles.length === 0) {
        toast.error('Please select at least one image');
        return;
      }

      // If only one image, use single upload
      if (galleryForm.imageFiles.length === 1) {
        const data = await uploadGalleryImage(
          galleryForm.imageFiles[0],
          galleryForm.title,
          galleryForm.category
        );

        if (data.success) {
          setGalleryItems([data.item, ...galleryItems]);
          setShowGalleryModal(false);
          setGalleryForm({
            title: '',
            description: '',
            category: 'competitions',
            mediaType: 'image',
            imageFiles: [],
            videoFile: null,
            videoUrl: ''
          });
          toast.success('Image uploaded successfully!');
        }
      } else {
        // Multiple images upload
        const data = await uploadMultipleGalleryImages(
          galleryForm.imageFiles,
          galleryForm.title,
          galleryForm.category
        );

        if (data.success) {
          setGalleryItems([...data.items, ...galleryItems]);
          setShowGalleryModal(false);
          setGalleryForm({
            title: '',
            description: '',
            category: 'competitions',
            mediaType: 'image',
            imageFiles: [],
            videoFile: null,
            videoUrl: ''
          });
          toast.success(`${data.items.length} images uploaded successfully!`);
        }
      }
    } catch (error) {
      toast.error(error.message || 'Failed to upload media');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteGalleryImage = async (id) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;
    
    try {
      const data = await deleteGalleryImage(id);
      
      if (data.success) {
        setGalleryItems(galleryItems.filter(item => item._id !== id));
        toast.success('Image deleted successfully!');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to delete image');
    }
  };

  const handleSendNotification = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const data = await adminAPI.sendNotification(notificationForm);
      
      setNotifications([data.notification, ...notifications]);
      setShowNotificationModal(false);
      setNotificationForm({ title: '', message: '', type: 'info', targetUsers: 'all' });
      toast.success('Notification sent successfully!');
    } catch (error) {
      toast.error(error.message || 'Failed to send notification');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateWinners = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Get the selected event details
      const selectedEvent = events.find(e => e._id === winnerForm.eventId);
      
      if (!selectedEvent) {
        toast.error('Selected event not found');
        setLoading(false);
        return;
      }

      // Prepare winners data with additional information
      const winnersData = winnerForm.winners
        .filter(winner => winner.userId && winner.prize) // Only include completed entries
        .map(winner => {
          // Get user details
          const selectedUser = users.find(u => u._id === winner.userId);
          
          return {
            position: winner.position,
            userId: winner.userId,
            winnerName: selectedUser ? (selectedUser.name || selectedUser.teamName || selectedUser.schoolName) : 'Unknown',
            email: selectedUser?.email || '',
            prize: winner.prize,
            eventId: winnerForm.eventId,
            eventTitle: selectedEvent.title,
            eventDate: selectedEvent.date, // Include event date
            createdAt: new Date().toISOString(), // Ensure proper timestamp
            updatedAt: new Date().toISOString()
          };
        });

      if (winnersData.length === 0) {
        toast.error('Please fill in at least one winner with both user and prize');
        setLoading(false);
        return;
      }


      // Send to backend
      const response = await adminAPI.updateEventWinners(winnerForm.eventId, winnersData);
      
      if (response.success) {
        toast.success(`Successfully updated ${winnersData.length} winner(s)!`);
        
        // Update local state with the new winners
        setWinners(prev => {
          // Remove any existing winners for this event
          const filteredWinners = prev.filter(w => w.eventId !== winnerForm.eventId);
          // Add the new winners
          return [...filteredWinners, ...winnersData];
        });
        
        setShowWinnerModal(false);
        resetWinnerForm();
        
        // Refresh dashboard data
        await fetchDashboardData(false);
      } else {
        throw new Error(response.message || 'Failed to update winners');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to update winners');
    } finally {
      setLoading(false);
    }
  };

  // Add helper function to reset winner form
  const resetWinnerForm = () => {
    setWinnerForm({ 
      eventId: '', 
      winners: [
        { position: '1st', userId: '', prize: '' },
        { position: '2nd', userId: '', prize: '' },
        { position: '3rd', userId: '', prize: '' }
      ]
    });
  };

  const resetEventForm = () => {
    setEventForm({
      title: '',
      description: '',
      date: '',
      endDate: '',
      time: '',
      endTime: '',
      timezone: 'EAT',
      location: '',
      type: 'tournament',
      levels: [],
      maxParticipants: 50,
      maxInstitutions: 10,
      prize: '',
      registrationFee: '',
      paymentRequired: false,
      featured: false,
      thumbnailFile: null
    });
  };

  const handleApproveUser = async (userId) => {
    try {
      await adminAPI.approveUser(userId);
      
      setUsers(users.map(user => 
        user._id === userId ? { ...user, status: 'active' } : user
      ));
      toast.success('User approved successfully!');
    } catch (error) {
      toast.error(error.message || 'Failed to approve user');
    }
  };

  const handleDeleteUser = async (userId) => {
    const user = users.find(u => u._id === userId);
    const userName = user?.name || user?.teamName || user?.schoolName || 'this user';
    
    if (!window.confirm(`Are you sure you want to permanently delete ${userName}? This action cannot be undone.`)) return;
    
    try {
      await adminAPI.deleteUser(userId);
      setUsers(users.filter(u => u._id !== userId));
      toast.success('User deleted successfully!');
    } catch (error) {
      toast.error(error.message || 'Failed to delete user');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    
    try {
      await adminAPI.deleteEvent(eventId);
      setEvents(events.filter(event => event._id !== eventId));
      toast.success('Event deleted successfully!');
    } catch (error) {
      toast.error(error.message || 'Failed to delete event');
    }
  };

  // Live Judging Handlers
  const handleCreateSession = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const data = await liveAPI.createSession(sessionForm);
      setLiveSessions([...liveSessions, data.session]);
      setShowSessionModal(false);
      setSessionForm({
        title: '', eventId: '', category: 'cultural', maxParticipants: 20,
        roundsCount: 3, timePerRound: 15, description: '', streamUrl: '', isLive: false
      });
      toast.success('Live session created successfully!');
    } catch (error) {
      toast.error(error.message || 'Failed to create session');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSession = async (sessionId) => {
    try {
      const data = await liveAPI.toggleSession(sessionId);
      setLiveSessions(liveSessions.map(session => 
        session._id === sessionId ? { ...session, isLive: data.session.isLive } : session
      ));
      toast.success(`Session ${data.session.isLive ? 'started' : 'stopped'} successfully!`);
    } catch (error) {
      toast.error(error.message || 'Failed to toggle session');
    }
  };

  const handleUpdateScore = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await liveAPI.updateScore(scoreForm);
      setShowScoreModal(false);
      setScoreForm({
        sessionId: '', userId: '', round: 1, technique: 0, composition: 0,
        creativity: 0, overall: 0, remarks: '', improvements: ''
      });
      toast.success('Score updated successfully!');
      
      // Refresh leaderboard if current session is being scored
      if (currentSession && scoreForm.sessionId === currentSession._id) {
        const leaderboardData = await liveAPI.getLeaderboard(currentSession._id);
        setLeaderboard(leaderboardData.leaderboard || []);
      }
    } catch (error) {
      toast.error(error.message || 'Failed to update score');
    } finally {
      setLoading(false);
    }
  };

  const handlePublishScores = async (sessionId, round) => {
    try {
      await liveAPI.publishScores(sessionId, round);
      toast.success(`Round ${round} scores published!`);
    } catch (error) {
      toast.error(error.message || 'Failed to publish scores');
    }
  };

  const fetchLeaderboard = async (sessionId) => {
    try {
      const data = await liveAPI.getLeaderboard(sessionId);
      setLeaderboard(data.leaderboard || []);
      setCurrentSession(liveSessions.find(s => s._id === sessionId));
    } catch (error) {
      toast.error('Failed to fetch leaderboard');
    }
  };

  

  // Handle navigation with automatic sidebar close on mobile
  const handleNavigation = (tabId) => {
    setActiveTab(tabId);
    // Auto-close sidebar on mobile after navigation
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  };

  const stats = [
    { 
      label: 'Total Users', 
      value: users.length, 
      color: 'from-blue-500 to-blue-600', 
      icon: FaUsers,
      change: `+${users.filter(u => new Date(u.createdAt) > new Date(Date.now() - 30*24*60*60*1000)).length} this month`
    },
    { 
      label: 'Active Events', 
      value: events.filter(e => new Date(e.date) > new Date()).length, 
      color: 'from-green-500 to-green-600', 
      icon: FaCalendarPlus,
      change: `${events.length} total events`
    },
    { 
      label: 'Pending Approvals', 
      value: users.filter(u => u.status === 'pending').length, 
      color: 'from-yellow-500 to-yellow-600', 
      icon: FaUserShield,
      change: 'Needs attention'
    },
    { 
      label: 'Live Sessions', 
      value: liveSessions.filter(s => s.isLive).length, 
      color: 'from-red-500 to-red-600', 
      icon: FaVideo,
      change: `${liveSessions.length} total sessions`
    }
  ];

  if (!isSuperAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FaUserShield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  const handleViewEvent = (event) => {
    // Create a detailed view modal or navigate to event details
    alert(`Viewing Event: ${event.title}\n\nDescription: ${event.description}\nDate: ${new Date(event.date).toLocaleDateString()}\nLocation: ${event.location}\nParticipants: ${event.participants?.length || 0}/${event.maxParticipants}`);
  };

  const handleEditEvent = (event) => {
    setEditingEvent(event);
    setEventForm({
      title: event.title || '',
      description: event.description || '',
      date: event.date ? new Date(event.date).toISOString().split('T')[0] : '',
      endDate: event.endDate ? new Date(event.endDate).toISOString().split('T')[0] : '',
      time: event.time || '',
      endTime: event.endTime || '',
      timezone: event.timezone || 'EAT',
      location: event.location || '',
      type: event.type || 'tournament',
      levels: event.levels || [],
      maxParticipants: event.maxParticipants || 50,
      maxInstitutions: event.maxInstitutions || 10,
      prize: event.prize || '',
      registrationFee: event.registrationFee || '',
      paymentRequired: event.paymentRequired || false,
      featured: event.featured || false,
      thumbnailFile: null
    });
    setShowEventModal(true);
  };

  const exportUsers = () => {
    const csvContent = [
      ['Name', 'Email', 'Type', 'Status', 'Joined Date'],
      ...users.map(user => [
        user.name || user.teamName || user.schoolName,
        user.email,
        user.registrationType,
        user.status,
        new Date(user.createdAt).toLocaleDateString()
      ])
    ].map(row => row.join(',')).join('\n');
    
    downloadCSV(csvContent, 'users-export');
  };

  const exportEvents = () => {
    const csvContent = [
      ['Title', 'Date', 'Location', 'Type', 'Participants', 'Max Participants', 'Status'],
      ...events.map(event => [
        event.title,
        new Date(event.date).toLocaleDateString(),
        event.location,
        event.type,
        event.participants?.length || 0,
        event.maxParticipants,
        event.status
      ])
    ].map(row => row.join(',')).join('\n');
    
    downloadCSV(csvContent, 'events-export');
  };

  const exportAnalytics = () => {
    const analyticsData = {
      totalUsers: users.length,
      activeUsers: users.filter(u => u.status === 'active').length,
      totalEvents: events.length,
      upcomingEvents: events.filter(e => new Date(e.date) > new Date()).length,
      liveSessions: liveSessions.length,
      activeSessions: liveSessions.filter(s => s.isLive).length,
      userGrowthThisMonth: users.filter(u => new Date(u.createdAt) > new Date(Date.now() - 30*24*60*60*1000)).length,
      averageParticipantsPerEvent: events.length > 0 ? Math.round(events.reduce((acc, e) => acc + (e.participants?.length || 0), 0) / events.length) : 0,
      conversionRate: users.length > 0 ? Math.round((users.filter(u => u.status === 'active').length / users.length) * 100) : 0,
      exportDate: new Date().toISOString()
    };
    
    const csvContent = [
      ['Metric', 'Value'],
      ...Object.entries(analyticsData).map(([key, value]) => [
        key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()),
        value
      ])
    ].map(row => row.join(',')).join('\n');
    
    downloadCSV(csvContent, 'analytics-export');
  };

  const exportSessions = () => {
    const csvContent = [
      ['Title', 'Category', 'Status', 'Participants', 'Max Participants', 'Rounds', 'Created Date'],
      ...liveSessions.map(session => [
        session.title,
        session.category,
        session.isLive ? 'Live' : 'Stopped',
        session.participants?.length || 0,
        session.maxParticipants,
        session.roundsCount,
        new Date(session.createdAt || Date.now()).toLocaleDateString()
      ])
    ].map(row => row.join(',')).join('\n');
    
    downloadCSV(csvContent, 'sessions-export');
  };

  const downloadCSV = (csvContent, filename) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Gallery API functions
  const fetchGallery = async () => {
    try {
      const response = await adminAPI.getGallery();
      return response.gallery || [];
    } catch (error) {
      console.error('Failed to fetch gallery:', error);
      return [];
    }
  };
  
  const uploadGalleryImage = async (imageFile, title, category) => {
    const formData = new FormData();
    formData.append('image', imageFile);
    formData.append('title', title);
    formData.append('category', category);

    return await adminAPI.uploadGalleryImage(formData);
  };

  const uploadMultipleGalleryImages = async (imageFiles, title, category) => {
    const formData = new FormData();

    // Append all images
    for (let i = 0; i < imageFiles.length; i++) {
      formData.append('images', imageFiles[i]);
    }

    formData.append('title', title);
    formData.append('category', category);

    return await adminAPI.uploadMultipleGalleryImages(formData);
  };

  const uploadGalleryVideo = async (videoFile, videoUrl, title, description, category) => {
    const formData = new FormData();

    if (videoFile) {
      formData.append('video', videoFile);
    }

    formData.append('title', title);
    formData.append('description', description || '');
    formData.append('category', category);

    if (videoUrl) {
      formData.append('videoUrl', videoUrl);
    }

    return await adminAPI.uploadGalleryVideo(formData);
  };

  const deleteGalleryImage = async (id) => {
    return await adminAPI.deleteGalleryImage(id);
  };


  return (
    <div className="min-h-screen bg-gray-50 flex pt-20">
      {/* Inject custom styles */}
      <style>{mobileStyles}</style>

      {/* Sidebar */}
      <motion.div
        initial={false}
        animate={{ 
          x: sidebarOpen ? 0 : -280,
          opacity: sidebarOpen ? 1 : 0
        }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        className={`
          fixed lg:relative
          top-0 left-0
          h-full
          w-64
          bg-white 
          border-r border-gray-200 
          shadow-lg 
          z-40
          ${sidebarOpen ? 'block' : 'hidden lg:block'}
        `}
        style={{
          marginTop: window.innerWidth < 1024 ? '80px' : '0' // Account for fixed header on mobile
        }}
      >
        {/* Sidebar Header */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            {sidebarOpen && (
              <div>
                <h2 className="text-lg font-bold text-gray-900">Admin Panel</h2>
                <p className="text-sm text-gray-600">Super Administrator</p>
              </div>
            )}
            {/* Hamburger menu - visible on all screens when sidebar is open */}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label={sidebarOpen ? "Close menu" : "Open menu"}
            >
              <FaBars className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        </div>
      
        {/* Sidebar Navigation */}
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 200px)' }}>
          {sidebarItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => handleNavigation(item.id)}
                className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-all duration-200 ${
                  activeTab === item.id
                    ? 'bg-green-100 text-green-700 border-l-4 border-green-500'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {sidebarOpen && <span className="font-medium">{item.label}</span>}
              </button>
            );
          })}
        </nav>
      
        {/* Sidebar Footer */}
        <div className="p-4 border-t border-gray-200">
          <button
            onClick={signOut}
            className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-red-600 hover:bg-red-50 transition-all duration-200"
          >
            <FaSignOutAlt className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && <span className="font-medium">Logout</span>}
          </button>
        </div>
      </motion.div>
      
      {/* Mobile Overlay - Only show on mobile when sidebar is open */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          style={{ marginTop: '80px' }} // Account for fixed header
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top Bar */}
        <div className="bg-white border-b border-gray-200 px-4 md:px-6 py-4 flex items-center justify-between sticky top-0 z-20">
          <div className="flex items-center space-x-4">
            {/* Hamburger - always visible on mobile, hidden on desktop */}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors lg:hidden"
              aria-label="Toggle menu"
            >
              <FaBars className="w-5 h-5 text-gray-600" />
            </button>
            
            <h1 className="text-xl md:text-2xl font-bold text-gray-900 capitalize">
              {activeTab.replace('-', ' ')} Dashboard
            </h1>
            
            {/* Refresh Button */}
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              title="Refresh data"
            >
              <FaSync className={`w-4 h-4 text-gray-600 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
          
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-600 hidden sm:inline">Welcome, Super Admin</span>
            <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
              <FaUserShield className="w-4 h-4 text-white" />
            </div>
          </div>
        </div>

        {/* Dashboard Content */}
        <div className="flex-1 p-4 md:p-6 overflow-y-auto">
          {loading && (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <FaSpinner className="w-8 h-8 text-green-500 animate-spin mx-auto mb-4" />
                <p className="text-gray-600">Loading dashboard data...</p>
              </div>
            </div>
          )}

          {/* TEAM TAB */}
          {!loading && activeTab === 'team' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">Team Members</h2>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={openCreateTeamModal}
                    className="px-4 py-2 bg-green-500 text-white rounded-2xl hover:bg-green-600 transition-colors"
                  >
                    <FaPlus className="w-3 h-3 inline-block mr-2" /> Add Member
                  </button>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-4 border border-gray-200">
                {teamMembers.length === 0 ? (
                  <div className="text-center py-12">
                    <FaUserTie className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No Team Members</h3>
                    <p className="text-gray-600">Add your first team member to display on the About page.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full table-auto border-collapse">
                      <thead>
                        <tr className="text-left text-xs text-gray-500">
                          <th className="px-4 py-3">Name</th>
                          <th className="px-4 py-3">Role</th>
                          <th className="px-4 py-3">Department</th>
                          <th className="px-4 py-3">Featured</th>
                          <th className="px-4 py-3">Active</th>
                          <th className="px-4 py-3">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {teamMembers.map(member => (
                          <tr key={member._id} className="border-t">
                            <td className="px-4 py-3 text-sm text-gray-700">{member.name}</td>
                            <td className="px-4 py-3 text-sm text-gray-700">{member.role}</td>
                            <td className="px-4 py-3 text-sm text-gray-700">{member.department}</td>
                            <td className="px-4 py-3 text-sm text-gray-700">{member.isFeatured ? 'Yes' : 'No'}</td>
                            <td className="px-4 py-3 text-sm text-gray-700">{member.isActive ? 'Yes' : 'No'}</td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <button onClick={() => openEditTeamModal(member)} className="px-2 py-1 bg-yellow-50 text-yellow-600 rounded text-xs">Edit</button>
                                <button onClick={() => handleDeleteTeamMember(member._id)} className="px-2 py-1 bg-red-50 text-red-600 rounded text-xs">Delete</button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Team Modal */}
              <AnimatePresence>
                {showTeamModal && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center">
                    <div className="absolute inset-0 bg-black/50" onClick={() => setShowTeamModal(false)}></div>
                    <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className="relative bg-white rounded-2xl w-full max-w-2xl p-6 z-10">
                      <h3 className="text-lg font-semibold mb-4">{editingTeamMember ? 'Edit' : 'Add'} Team Member</h3>
                      <form onSubmit={handleTeamSubmit} className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <input name="name" value={teamForm.name} onChange={handleTeamFormChange} placeholder="Full name" className="px-3 py-2 border rounded" required />
                          <input name="role" value={teamForm.role} onChange={handleTeamFormChange} placeholder="Role" className="px-3 py-2 border rounded" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <input name="email" value={teamForm.email} onChange={handleTeamFormChange} placeholder="Email" className="px-3 py-2 border rounded" />
                          <input name="phone" value={teamForm.phone} onChange={handleTeamFormChange} placeholder="Phone" className="px-3 py-2 border rounded" />
                        </div>
                        <div>
                          <input name="department" value={teamForm.department} onChange={handleTeamFormChange} placeholder="Department" className="px-3 py-2 border rounded w-full" />
                        </div>
                        <div>
                          <textarea name="bio" value={teamForm.bio} onChange={handleTeamFormChange} placeholder="Short bio" className="px-3 py-2 border rounded w-full" rows={4} />
                        </div>
                        <div>
                          <label className="text-sm text-gray-600">Expertise (comma separated)</label>
                          <input name="expertise" value={Array.isArray(teamForm.expertise) ? teamForm.expertise.join(', ') : teamForm.expertise} onChange={(e)=>setTeamForm(prev=>({...prev, expertise: e.target.value}))} placeholder="e.g. Coaching,Youth Development" className="px-3 py-2 border rounded w-full" />
                        </div>
                        <div className="flex items-center gap-3">
                          <label className="flex items-center gap-2"><input type="checkbox" name="isFeatured" checked={teamForm.isFeatured} onChange={handleTeamFormChange} /> Featured</label>
                          <label className="flex items-center gap-2"><input type="checkbox" name="isActive" checked={teamForm.isActive} onChange={handleTeamFormChange} /> Active</label>
                        </div>
                        <div>
                          <label className="block text-sm text-gray-600 mb-1">Photo</label>
                          <input type="file" accept="image/*" onChange={handleTeamFileChange} />
                        </div>

                        <div className="flex items-center justify-end gap-3 mt-4">
                          <button type="button" onClick={()=>setShowTeamModal(false)} className="px-4 py-2 bg-gray-100 rounded">Cancel</button>
                          <button type="submit" className="px-4 py-2 bg-green-600 text-white rounded">Save</button>
                        </div>
                      </form>
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

            {/* PARTNERS / SPONSORS TAB */}
            {!loading && activeTab === 'partners' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-gray-900">Partners / Sponsors</h2>
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={openCreateSponsorModal}
                      className="px-4 py-2 bg-green-500 text-white rounded-2xl hover:bg-green-600 transition-colors"
                    >
                      <FaPlus className="w-3 h-3 inline-block mr-2" /> Add Partner
                    </button>
                  </div>
                </div>

                <div className="bg-white rounded-2xl p-4 border border-gray-200">
                  {sponsorsList.length === 0 ? (
                    <div className="text-center py-12">
                      <FaHandshake className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">No Partners</h3>
                      <p className="text-gray-600">Add partners to show on the public site.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {sponsorsList.map(s => (
                        <div key={s._id} className="flex items-center justify-between p-4 border rounded-lg">
                          <div className="flex items-center space-x-4">
                            {s.logo ? <img src={s.logo} alt={s.name} className="max-h-12 object-contain" /> : <div className="text-sm font-medium text-gray-700">{s.name}</div>}
                            <div>
                              <div className="text-sm font-semibold">{s.name}</div>
                              <div className="text-xs text-gray-500">{s.website}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button onClick={() => openEditSponsorModal(s)} className="px-2 py-1 bg-yellow-50 text-yellow-600 rounded text-xs">Edit</button>
                            <button onClick={() => handleDeleteSponsor(s._id)} className="px-2 py-1 bg-red-50 text-red-600 rounded text-xs">Delete</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Sponsor Modal */}
                <AnimatePresence>
                  {showSponsorModal && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center">
                      <div className="absolute inset-0 bg-black/50" onClick={() => setShowSponsorModal(false)}></div>
                      <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className="relative bg-white rounded-2xl w-full max-w-2xl p-6 z-10">
                        <h3 className="text-lg font-semibold mb-4">{editingSponsor ? 'Edit' : 'Add'} Partner</h3>
                        <form onSubmit={handleSponsorSubmit} className="space-y-3">
                          <div className="grid grid-cols-2 gap-3">
                            <input name="name" value={sponsorForm.name} onChange={handleSponsorFormChange} placeholder="Name" className="px-3 py-2 border rounded" required />
                            <input name="website" value={sponsorForm.website} onChange={handleSponsorFormChange} placeholder="Website" className="px-3 py-2 border rounded" />
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <select name="tier" value={sponsorForm.tier} onChange={handleSponsorFormChange} className="px-3 py-2 border rounded">
                              <option value="partner">Partner</option>
                              <option value="sponsor">Sponsor</option>
                              <option value="supporter">Supporter</option>
                            </select>
                            <select name="type" value={sponsorForm.type} onChange={handleSponsorFormChange} className="px-3 py-2 border rounded">
                              <option value="organization">Organization</option>
                              <option value="individual">Individual</option>
                            </select>
                          </div>
                          <div>
                            <textarea name="notes" value={sponsorForm.notes} onChange={handleSponsorFormChange} placeholder="Notes" className="px-3 py-2 border rounded w-full" rows={3} />
                          </div>
                          <div className="flex items-center gap-3">
                            <label className="flex items-center gap-2"><input type="checkbox" name="isFeatured" checked={sponsorForm.isFeatured} onChange={handleSponsorFormChange} /> Featured</label>
                            <label className="flex items-center gap-2"><input type="checkbox" name="isActive" checked={sponsorForm.isActive} onChange={handleSponsorFormChange} /> Active</label>
                          </div>
                          <div>
                            <label className="block text-sm text-gray-600 mb-1">Logo</label>
                            <input type="file" accept="image/*" onChange={handleSponsorFileChange} />
                          </div>

                          <div className="flex items-center justify-end gap-3 mt-4">
                            <button type="button" onClick={()=>setShowSponsorModal(false)} className="px-4 py-2 bg-gray-100 rounded">Cancel</button>
                            <button type="submit" className="px-4 py-2 bg-green-600 text-white rounded">Save</button>
                          </div>
                        </form>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

          {/* OVERVIEW TAB */}
          {!loading && activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
                {stats.map((stat, index) => {
                  const Icon = stat.icon;
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="bg-white rounded-3xl border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className={`p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}>
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-2xl">
                          {stat.change}
                        </span>
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">
                        {stat.value}
                      </h3>
                      <p className="text-gray-600 text-sm">
                        {stat.label}
                      </p>
                    </motion.div>
                  );
                })}
              </div>

              {/* Quick Actions */}
              <div className="bg-white rounded-3xl border border-gray-200 p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <button
                    onClick={() => setShowEventModal(true)}
                    className="flex items-center justify-center space-x-2 p-4 bg-green-50 border border-green-200 rounded-2xl hover:bg-green-100 transition-colors"
                  >
                    <FaPlus className="w-5 h-5 text-green-600" />
                    <span className="font-medium text-green-700">Create Event</span>
                  </button>
                  
                  {/* NEW: Create Program Button */}
                  <button
                    onClick={() => setShowProgramModal(true)}
                    className="flex items-center justify-center space-x-2 p-4 bg-purple-50 border border-purple-200 rounded-2xl hover:bg-purple-100 transition-colors"
                  >
                    <FaGraduationCap className="w-5 h-5 text-purple-600" />
                    <span className="font-medium text-purple-700">Create Program</span>
                  </button>
                  
                  <button
                    onClick={() => setShowNotificationModal(true)}
                    className="flex items-center justify-center space-x-2 p-4 bg-blue-50 border border-blue-200 rounded-2xl hover:bg-blue-100 transition-colors"
                  >
                    <FaBell className="w-5 h-5 text-blue-600" />
                    <span className="font-medium text-blue-700">Send Notification</span>
                  </button>
                  
                  <button
                    onClick={() => setShowWinnerModal(true)}
                    className="flex items-center justify-center space-x-2 p-4 bg-yellow-50 border border-yellow-200 rounded-2xl hover:bg-yellow-100 transition-colors"
                  >
                    <FaTrophy className="w-5 h-5 text-yellow-600" />
                    <span className="font-medium text-yellow-700">Update Winners</span>
                  </button>
                </div>
              </div>

              {/* Recent Activity */}
              <div className="bg-white rounded-3xl border border-gray-200 p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
                <div className="space-y-3">
                  {users.slice(-5).map((user, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-2xl">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                          <FaUsers className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {user.name || user.teamName || user.schoolName} registered
                          </p>
                          <p className="text-sm text-gray-600">
                            {formatRelativeDate(user.createdAt)}
                          </p>
                        </div>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        user.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {user.status}
                      </span>
                    </div>
                  ))}
                  {users.length === 0 && (
                    <div className="text-center py-4 text-gray-500">
                      <FaUsers className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                      <p className="text-sm">No recent activity</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* EVENTS TAB - ENHANCED WITH ALL NEW FIELDS */}
          {!loading && activeTab === 'events' && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <h2 className="text-2xl font-bold text-gray-900">Event Management</h2>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => setShowArchivedEvents(!showArchivedEvents)}
                    className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg transition-colors text-sm ${
                      showArchivedEvents 
                        ? 'bg-gray-500 text-white hover:bg-gray-600' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <FaArchive className="w-3 h-3" />
                    <span>{showArchivedEvents ? 'Hide Archived' : 'Show Archived'}</span>
                  </button>
                  <button
                    onClick={() => setShowEventModal(true)}
                    className="flex items-center space-x-2 px-4 py-2 bg-green-500 text-white rounded-2xl hover:bg-green-600 transition-colors"
                  >
                    <FaPlus className="w-4 h-4" />
                    <span>Create Event</span>
                  </button>
                </div>
              </div>
          
              <div className="space-y-4">
                {events
                  .filter(event => showArchivedEvents ? event.isArchived : !event.isArchived)
                  .map((event) => (
                  <div key={event._id} className="bg-white rounded-2xl border border-gray-200 p-4 md:p-6 shadow-sm">
                    {/* Event Header */}
                    <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4 mb-4">
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-3">
                          <h3 className="text-lg md:text-xl font-semibold text-gray-900">{event.title}</h3>
                          
                          {event.featured && (
                            <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
                              ⭐ Featured
                            </span>
                          )}
                          
                          {event.isArchived && (
                            <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                              📦 Archived
                            </span>
                          )}
                          
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            event.type === 'tournament' ? 'bg-purple-100 text-purple-800' :
                            event.type === 'workshop' ? 'bg-blue-100 text-blue-800' :
                            event.type === 'training' ? 'bg-green-100 text-green-800' :
                            event.type === 'conference' ? 'bg-indigo-100 text-indigo-800' :
                            'bg-orange-100 text-orange-800'
                          }`}>
                            {event.type.charAt(0).toUpperCase() + event.type.slice(1)}
                          </span>
          
                          {event.paymentRequired && (
                            <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                              💳 Payment Required
                            </span>
                          )}
                        </div>
                        
                        <p className="text-gray-600 mb-3 text-sm md:text-base line-clamp-2">{event.description}</p>
                        
                        {/* Event Details Grid */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 text-xs md:text-sm mb-3">
                          <div>
                            <span className="text-gray-500 block mb-1">📅 Event Period:</span>
                            <p className="font-medium">
                              {formatDate(event.date)}
                              {event.endDate && event.endDate !== event.date && (
                                <span className="text-green-600"> → {formatDate(event.endDate)}</span>
                              )}
                            </p>
                          </div>
                          <div>
                            <span className="text-gray-500 block mb-1">🕐 Time:</span>
                            <p className="font-medium">
                              {event.time}
                              {event.endTime && ` - ${event.endTime}`}
                              <span className="text-xs text-gray-500 ml-1">({event.timezone})</span>
                            </p>
                          </div>
                          <div>
                            <span className="text-gray-500 block mb-1">📍 Location:</span>
                            <p className="font-medium truncate" title={event.location}>{event.location}</p>
                          </div>
                          <div>
                            <span className="text-gray-500 block mb-1">🏆 Prize:</span>
                            <p className="font-medium truncate" title={event.prize}>{event.prize || 'N/A'}</p>
                          </div>
                        </div>
          
                        {/* Levels & Registration Info */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-3 bg-gray-50 rounded-lg">
                          <div>
                            <span className="text-gray-500 text-xs block mb-1">Eligible Levels:</span>
                            <div className="flex flex-wrap gap-1">
                              {event.levels && event.levels.length > 0 ? (
                                event.levels.map((level, idx) => (
                                  <span key={idx} className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                                    {level === 'juniors' ? 'Juniors' :
                                     level === 'seniors' ? 'Seniors' :
                                     level === 'university' ? 'University' :
                                     level === 'working' ? 'Working' :
                                     level === 'all' ? 'All Levels' : level}
                                  </span>
                                ))
                              ) : (
                                <span className="text-xs text-gray-500">All levels</span>
                              )}
                            </div>
                          </div>
                          
                          <div>
                            <span className="text-gray-500 text-xs block mb-1">Capacity:</span>
                            <p className="font-medium text-sm">
                              👥 {event.maxParticipants} individuals
                              {event.maxInstitutions > 0 && ` | 🏫 ${event.maxInstitutions} institutions`}
                            </p>
                          </div>
                          
                          <div>
                            <span className="text-gray-500 text-xs block mb-1">Registration Fee:</span>
                            <p className="font-medium text-sm">
                              {event.registrationFee || 'Free'}
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Action Buttons - More Visible */}
                      <div className="flex md:flex-col gap-2 md:ml-4 flex-shrink-0">
                        <button
                          onClick={() => handleViewEvent(event)}
                          className="flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium shadow-sm hover:shadow-md"
                          title="View Details"
                        >
                          <FaEye className="w-4 h-4" />
                          <span className="hidden lg:inline">View</span>
                        </button>
                        <button
                          onClick={() => handleEditEvent(event)}
                          className="flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium shadow-sm hover:shadow-md"
                          title="Edit Event"
                        >
                          <FaEdit className="w-4 h-4" />
                          <span className="hidden lg:inline">Edit</span>
                        </button>
                        <button
                          onClick={() => handleArchiveEvent(event._id)}
                          className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg transition-colors text-sm font-medium shadow-sm hover:shadow-md ${
                            event.isArchived
                              ? 'bg-green-600 text-white hover:bg-green-700'
                              : 'bg-yellow-600 text-white hover:bg-yellow-700'
                          }`}
                          title={event.isArchived ? 'Unarchive' : 'Archive'}
                        >
                          <FaArchive className="w-4 h-4" />
                          <span className="hidden lg:inline">{event.isArchived ? 'Restore' : 'Archive'}</span>
                        </button>
                        <button
                          onClick={() => handleDeleteEvent(event._id)}
                          className="flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium shadow-sm hover:shadow-md"
                          title="Delete Event"
                        >
                          <FaTrash className="w-4 h-4" />
                          <span className="hidden lg:inline">Delete</span>
                        </button>
                      </div>
                    </div>
          
                    {/* Participants Section */}
                    <div className="border-t border-gray-200 pt-4">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                        <h4 className="text-base md:text-lg font-semibold text-gray-900">
                          Registered Participants ({event.participants?.length || 0}/{event.maxParticipants})
                        </h4>
                        <div className="flex items-center space-x-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            (event.participants?.length || 0) >= event.maxParticipants ? 'bg-red-100 text-red-800' :
                            (event.participants?.length || 0) > event.maxParticipants * 0.8 ? 'bg-yellow-100 text-yellow-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {(event.participants?.length || 0) >= event.maxParticipants ? 'Full' :
                             (event.participants?.length || 0) > event.maxParticipants * 0.8 ? 'Almost Full' :
                             'Available'}
                          </span>
                          <div className="w-24 bg-gray-200 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full transition-all ${
                                (event.participants?.length || 0) >= event.maxParticipants ? 'bg-red-500' :
                                (event.participants?.length || 0) > event.maxParticipants * 0.8 ? 'bg-yellow-500' :
                                'bg-green-500'
                              }`}
                              style={{ 
                                width: `${Math.min(((event.participants?.length || 0) / event.maxParticipants) * 100, 100)}%` 
                              }}
                            />
                          </div>
                        </div>
                      </div>
          
                      {/* Desktop Table - Participants */}
                      {event.participants && event.participants.length > 0 ? (
                        <>
                          <div className="hidden md:block overflow-x-auto rounded-lg border border-gray-200" style={{ maxWidth: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                            <table className="w-full text-sm min-w-full participant-table">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Participant</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                  {/* ✅ Show Payment column if event has a registration fee */}
                                  {event.registrationFee && event.registrationFee !== 'Free' && event.registrationFee.toLowerCase() !== 'free' && (
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Payment</th>
                                  )}
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Registered</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200">
                                {event.participants
                                  .filter(participant => participant.userId)
                                  .map((participant, index) => (
                                    <tr key={participant._id || index} className="hover:bg-gray-50">
                                      <td className="px-4 py-3">
                                        <div className="flex items-center space-x-2">
                                          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                                            <span className="text-white text-xs font-semibold">
                                              {(() => {
                                                const name = participant.userId?.name || 
                                                           participant.userId?.teamName || 
                                                           participant.userId?.schoolName || 
                                                           'Unknown User';
                                                return name[0]?.toUpperCase() || 'U';
                                              })()}
                                            </span>
                                          </div>
                                          <div>
                                            <p className="font-medium text-gray-900">
                                              {participant.userId?.name || 
                                               participant.userId?.teamName || 
                                               participant.userId?.schoolName || 
                                               'Unknown User'}
                                            </p>
                                          </div>
                                        </div>
                                      </td>
                                      <td className="px-4 py-3 text-gray-600">
                                        {participant.userId?.email || 'No email'}
                                      </td>
                                      <td className="px-4 py-3 text-gray-600">
                                        {participant.userId?.phone || 'No phone'}
                                      </td>
                                      <td className="px-4 py-3">
                                        <span className="capitalize text-gray-900">
                                          {participant.userId?.registrationType || 'Unknown'}
                                        </span>
                                      </td>
                                      <td className="px-4 py-3">
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                          participant.userId?.status === 'active' ? 'bg-green-100 text-green-800' :
                                          participant.userId?.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                          'bg-red-100 text-red-800'
                                        }`}>
                                          {participant.userId?.status || 'Unknown'}
                                        </span>
                                      </td>
                                     
                                      {/* ✅ Payment Status Cell */}
                                      {event.registrationFee && event.registrationFee !== 'Free' && event.registrationFee.toLowerCase() !== 'free' && (
                                        <td className="px-4 py-3">
                                          <PaymentStatusBadge
                                            participant={participant}
                                            eventId={event._id}
                                            eventFee={event.registrationFee}
                                            currency={event.registrationCurrency || 'KES'}
                                            onUpdate={(updatedParticipant) => {
                                              setEvents(events.map(e => {
                                                if (e._id === event._id) {
                                                  return {
                                                    ...e,
                                                    participants: e.participants.map(p => 
                                                      p._id === updatedParticipant._id ? updatedParticipant : p
                                                    )
                                                  };
                                                }
                                                return e;
                                              }));
                                            }}
                                          />
                                        </td>
                                      )}
                                      
                                      <td className="px-4 py-3 text-gray-600 text-xs">
                                        {formatRelativeDate(participant.registeredAt || participant.userId?.createdAt || Date.now())}
                                      </td>
                                      
                                      {/* ✅ NEW: Actions Cell with Unregister Button */}
                                      <td className="px-4 py-3">
                                        <button
                                          onClick={() => {
                                            if (window.confirm(`Remove ${participant.userId?.name || participant.userId?.teamName || participant.userId?.schoolName || 'this participant'} from the event?`)) {
                                              handleUnregisterParticipant(event._id, participant._id);
                                            }
                                          }}
                                          className="px-2 py-1 bg-red-50 text-red-600 rounded hover:bg-red-100 transition-colors text-xs flex items-center space-x-1"
                                          title="Remove participant from event"
                                        >
                                          <FaTimes className="w-3 h-3" />
                                          <span>Remove</span>
                                        </button>
                                      </td>
                                    </tr>
                                  ))}
                              </tbody>
                            </table>
                          </div>
                      
                          {/* Mobile Cards - Participants */}
                          <div className="md:hidden space-y-3">
                            {event.participants
                              .filter(participant => participant.userId)
                              .map((participant, index) => (
                                <div key={participant._id || index} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                                  <div className="flex items-start space-x-3 mb-3">
                                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                                      <span className="text-white text-sm font-semibold">
                                        {(() => {
                                          const name = participant.userId?.name || 
                                                     participant.userId?.teamName || 
                                                     participant.userId?.schoolName || 
                                                     'Unknown User';
                                          return name[0]?.toUpperCase() || 'U';
                                        })()}
                                      </span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="font-semibold text-gray-900 text-sm truncate">
                                        {participant.userId?.name || 
                                         participant.userId?.teamName || 
                                         participant.userId?.schoolName || 
                                         'Unknown User'}
                                      </p>
                                      <p className="text-xs text-gray-600 truncate">{participant.userId?.email || 'No email'}</p>
                                    </div>
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                                      participant.userId?.status === 'active' ? 'bg-green-100 text-green-800' :
                                      participant.userId?.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                      'bg-red-100 text-red-800'
                                    }`}>
                                      {participant.userId?.status || 'Unknown'}
                                    </span>
                                  </div>
                                  
                                  <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                                    <div>
                                      <span className="text-gray-500">Phone:</span>
                                      <p className="font-medium truncate">{participant.userId?.phone || 'N/A'}</p>
                                    </div>
                                    <div>
                                      <span className="text-gray-500">Type:</span>
                                      <p className="font-medium capitalize">{participant.userId?.registrationType || 'Unknown'}</p>
                                    </div>
                                    <div className="col-span-2">
                                      <span className="text-gray-500">Registered:</span>
                                      <p className="font-medium">{formatRelativeDate(participant.registeredAt || participant.userId?.createdAt || Date.now())}</p>
                                    </div>
                                  </div>
                      
                                  {/* ✅ Payment Section for Mobile */}
                                  {event.registrationFee && event.registrationFee !== 'Free' && event.registrationFee.toLowerCase() !== 'free' && (
                                    <div className="border-t border-gray-200 pt-2 mb-2">
                                      <PaymentStatusBadge
                                        participant={participant}
                                        eventId={event._id}
                                        eventFee={event.registrationFee}
                                        currency={event.registrationCurrency || 'KES'}
                                        mobile={true}
                                        onUpdate={(updatedParticipant) => {
                                          setEvents(events.map(e => {
                                            if (e._id === event._id) {
                                              return {
                                                ...e,
                                                participants: e.participants.map(p => 
                                                  p._id === updatedParticipant._id ? updatedParticipant : p
                                                )
                                              };
                                            }
                                            return e;
                                          }));
                                        }}
                                      />
                                    </div>
                                  )}
                                  
                                  {/* ✅ Unregister Button for Mobile */}
                                  <div className="border-t border-gray-200 pt-2">
                                    <button
                                      onClick={() => {
                                        if (window.confirm(`Remove ${participant.userId?.name || participant.userId?.teamName || participant.userId?.schoolName || 'this participant'} from the event?`)) {
                                          handleUnregisterParticipant(event._id, participant._id);
                                        }
                                      }}
                                      className="w-full px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors text-xs font-medium flex items-center justify-center space-x-1"
                                    >
                                      <FaTimes className="w-3 h-3" />
                                      <span>Remove from Event</span>
                                    </button>
                                  </div>
                                </div>
                              ))}
                          </div>
                        </>
                      ) : (
                        <div className="text-center py-8">
                          <FaUsers className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                          <h4 className="text-sm font-medium text-gray-900 mb-1">No Participants Yet</h4>
                          <p className="text-gray-600 text-xs">Participants will appear here as they register</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
          
                {events.filter(event => showArchivedEvents ? event.isArchived : !event.isArchived).length === 0 && (
                  <div className="text-center py-12">
                    <FaCalendarPlus className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {showArchivedEvents ? 'No Archived Events' : 'No Events Found'}
                    </h3>
                    <p className="text-gray-600 mb-4">
                      {showArchivedEvents 
                        ? 'No events have been archived yet' 
                        : 'Create your first event to get started'}
                    </p>
                    {!showArchivedEvents && (
                      <button
                        onClick={() => setShowEventModal(true)}
                        className="px-6 py-2 bg-green-500 text-white rounded-2xl hover:bg-green-600 transition-colors"
                      >
                        Create First Event
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* LIVE JUDGING TAB */}
          {!loading && activeTab === 'live-judging' && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <h2 className="text-2xl font-bold text-gray-900">Live Judging & Adjudication</h2>
                <button
                  onClick={() => setShowSessionModal(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-red-500 text-white rounded-2xl hover:bg-red-600 transition-colors"
                >
                  <FaVideo className="w-4 h-4" />
                  <span>Create Live Session</span>
                </button>
              </div>

              {/* Active Sessions Panel */}
              <div className="bg-white rounded-3xl border border-gray-200 p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                    <FaBroadcastTower className="w-5 h-5 text-red-500" />
                    <span>Live Sessions ({liveSessions.filter(s => s.isLive).length} active)</span>
                  </h3>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setShowScoreModal(true)}
                      className="flex items-center space-x-2 px-3 py-1.5 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm"
                    >
                      <FaStar className="w-3 h-3" />
                      <span>Update Scores</span>
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                  {liveSessions.map((session) => (
                    <div key={session._id} className={`p-4 rounded-2xl border-2 transition-all ${
                      session.isLive 
                        ? 'bg-red-50 border-red-200 shadow-md' 
                        : 'bg-gray-50 border-gray-200'
                    }`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          <div className={`w-3 h-3 rounded-full ${
                            session.isLive ? 'bg-red-500 animate-pulse' : 'bg-gray-400'
                          }`} />
                          <h4 className="font-semibold text-gray-900 text-sm">{session.title}</h4>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          session.isLive 
                            ? 'bg-red-100 text-red-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {session.isLive ? 'LIVE' : 'STOPPED'}
                        </span>
                      </div>

                      <div className="space-y-2 text-xs text-gray-600 mb-3">
                        <p>Category: <span className="font-medium capitalize">{session.category}</span></p>
                        <p>Participants: <span className="font-medium">{session.participants?.length || 0}/{session.maxParticipants}</span></p>
                        <p>Rounds: <span className="font-medium">{session.currentRound || 1}/{session.roundsCount}</span></p>
                        <p>Time/Round: <span className="font-medium">{session.timePerRound} min</span></p>
                      </div>

                      <div className="flex space-x-1">
                        <button
                          onClick={() => handleToggleSession(session._id)}
                          className={`flex-1 px-2 py-1.5 rounded-lg transition-colors text-xs font-medium ${
                            session.isLive
                              ? 'bg-red-500 text-white hover:bg-red-600'
                              : 'bg-green-500 text-white hover:bg-green-600'
                          }`}
                        >
                          {session.isLive ? (
                            <>
                              <FaStop className="w-3 h-3 mx-auto mb-1" />
                              Stop
                            </>
                          ) : (
                            <>
                              <FaPlay className="w-3 h-3 mx-auto mb-1" />
                              Start
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => fetchLeaderboard(session._id)}
                          className="flex-1 px-2 py-1.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-xs"
                        >
                          <FaEye className="w-3 h-3 mx-auto mb-1" />
                          View
                        </button>
                        <button
                          onClick={() => handlePublishScores(session._id, session.currentRound || 1)}
                          className="flex-1 px-2 py-1.5 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors text-xs"
                        >
                          <FaUpload className="w-3 h-3 mx-auto mb-1" />
                          Publish
                        </button>
                      </div>

                      {session.streamUrl && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <a
                            href={session.streamUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 text-xs"
                          >
                            <FaVideo className="w-3 h-3" />
                            <span>View Stream</span>
                          </a>
                        </div>
                      )}
                    </div>
                  ))}

                  {liveSessions.length === 0 && (
                    <div className="col-span-full text-center py-8">
                      <FaVideo className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                      <h4 className="font-medium text-gray-900 mb-1">No Live Sessions</h4>
                      <p className="text-gray-600 text-sm mb-4">Create your first live judging session</p>
                      <button
                        onClick={() => setShowSessionModal(true)}
                        className="px-4 py-2 bg-red-500 text-white rounded-2xl hover:bg-red-600 transition-colors"
                      >
                        Create Session
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Live Leaderboard */}
              {currentSession && (
                <div className="bg-white rounded-3xl border border-gray-200 p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                      <FaTrophy className="w-5 h-5 text-yellow-500" />
                      <span>Live Leaderboard - {currentSession.title}</span>
                    </h3>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-600">Round {currentSession.currentRound || 1}</span>
                      <button
                        onClick={() => fetchLeaderboard(currentSession._id)}
                        className="p-2 text-gray-600 hover:text-gray-800 transition-colors"
                      >
                        <FaSync className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Rank</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Participant</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Technique</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Composition</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Creativity</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Overall</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {leaderboard.map((participant, index) => (
                          <tr key={participant.userId} className={`hover:bg-gray-50 ${
                            index === 0 ? 'bg-yellow-50' : index === 1 ? 'bg-gray-50' : index === 2 ? 'bg-orange-50' : ''
                          }`}>
                            <td className="px-4 py-3">
                              <div className="flex items-center space-x-2">
                                {index === 0 && <FaCrown className="w-4 h-4 text-yellow-500" />}
                                {index === 1 && <FaMedal className="w-4 h-4 text-gray-500" />}
                                {index === 2 && <FaAward className="w-4 h-4 text-orange-500" />}
                                <span className="font-semibold text-gray-900">#{index + 1}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center space-x-2">
                                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                                  <span className="text-white text-xs font-semibold">
                                    {(participant.name || 'U')[0].toUpperCase()}
                                  </span>
                                </div>
                                <div>
                                  <p className="font-medium text-gray-900 text-sm">{participant.name}</p>
                                  <p className="text-xs text-gray-600">{participant.category}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                              {participant.scores?.technique || 0}/100
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                              {participant.scores?.composition || 0}/100
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                              {participant.scores?.creativity || 0}/100
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                              {participant.scores?.overall || 0}/100
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-lg font-bold text-green-600">
                                {participant.totalScore || 0}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <button
                                onClick={() => {
                                  setScoreForm({
                                    ...scoreForm,
                                    sessionId: currentSession._id,
                                    userId: participant.userId
                                  });
                                  setShowScoreModal(true);
                                }}
                                className="px-3 py-1 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-xs"
                              >
                                Score
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {leaderboard.length === 0 && (
                    <div className="text-center py-8">
                      <FaUserTie className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-gray-600 text-sm">No participants scored yet</p>
                    </div>
                  )}
                </div>
              )}

              {/* Adjudication Tools */}
              <div className="bg-white rounded-3xl border border-gray-200 p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                  <FaClipboardList className="w-5 h-5 text-purple-500" />
                  <span>Adjudication Tools</span>
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 bg-purple-50 border border-purple-200 rounded-2xl">
                    <h4 className="font-semibold text-purple-900 mb-2">Scoring Criteria</h4>
                    <div className="space-y-1 text-sm text-purple-800">
                      <p>• Technique (0-100): Technical skill and execution</p>
                      <p>• Composition (0-100): Structure and flow</p>
                      <p>• Creativity (0-100): Innovation and originality</p>
                      <p>• Overall (0-100): General impression</p>
                    </div>
                  </div>

                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-2xl">
                    <h4 className="font-semibold text-blue-900 mb-2">Round Management</h4>
                    <div className="space-y-2">
                      <button className="w-full px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm">
                        Start Next Round
                      </button>
                      <button className="w-full px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm">
                        Publish Results
                      </button>
                    </div>
                  </div>

                  <div className="p-4 bg-green-50 border border-green-200 rounded-2xl">
                    <h4 className="font-semibold text-green-900 mb-2">Quick Actions</h4>
                    <div className="space-y-2">
                      <button 
                        onClick={() => setShowSessionModal(true)}
                        className="w-full px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm"
                      >
                        New Session
                      </button>
                      <button 
                        onClick={() => setShowScoreModal(true)}
                        className="w-full px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm"
                      >
                        Add Score
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Recent Sessions History */}
              <div className="bg-white rounded-3xl border border-gray-200 p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Sessions</h3>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {liveSessions.slice().reverse().map((session) => (
                    <div key={session._id} className="flex items-center justify-between p-3 bg-gray-50 rounded-2xl">
                      <div className="flex items-center space-x-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          session.isLive ? 'bg-red-500' : 'bg-gray-400'
                        }`}>
                          <FaVideo className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 text-sm">{session.title}</p>
                          <p className="text-xs text-gray-600">
                            {session.participants?.length || 0} participants • {session.category}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          session.isLive ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {session.isLive ? 'Live' : 'Ended'}
                        </span>
                        <button
                          onClick={() => fetchLeaderboard(session._id)}
                          className="px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600 transition-colors"
                        >
                          View
                        </button>
                      </div>
                    </div>
                  ))}

                  {liveSessions.length === 0 && (
                    <div className="text-center py-4 text-gray-500">
                      <FaGamepad className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                      <p className="text-sm">No sessions created yet</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* PROGRAMS TAB - ENHANCED WITH ENROLLED MEMBERS VIEW & PAYMENT TRACKING */}
          {!loading && activeTab === 'programs' && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <h2 className="text-2xl font-bold text-gray-900">Programs Management</h2>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => setShowArchivedPrograms(!showArchivedPrograms)}
                    className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg transition-colors text-sm ${
                      showArchivedPrograms 
                        ? 'bg-gray-500 text-white hover:bg-gray-600' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <FaArchive className="w-3 h-3" />
                    <span>{showArchivedPrograms ? 'Hide Archived' : 'Show Archived'}</span>
                  </button>
                  <button
                    onClick={() => setShowProgramModal(true)}
                    className="flex items-center space-x-2 px-4 py-2 bg-green-500 text-white rounded-2xl hover:bg-green-600 transition-colors"
                  >
                    <FaPlus className="w-4 h-4" />
                    <span>Create Program</span>
                  </button>
                </div>
              </div>
          
              {/* Programs Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                <div className="bg-white rounded-2xl border border-gray-200 p-3 md:p-4 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xs md:text-sm font-medium text-gray-500">Total Programs</h3>
                    <FaGraduationCap className="w-3 h-3 md:w-4 md:h-4 text-blue-500" />
                  </div>
                  <p className="text-xl md:text-2xl font-bold text-gray-900">{programs.length}</p>
                  <p className="text-xs text-gray-600">
                    {programs.filter(p => !p.isArchived).length} active
                  </p>
                </div>
                
                <div className="bg-white rounded-2xl border border-gray-200 p-3 md:p-4 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xs md:text-sm font-medium text-gray-500">Total Enrolled</h3>
                    <FaUsers className="w-3 h-3 md:w-4 md:h-4 text-green-500" />
                  </div>
                  <p className="text-xl md:text-2xl font-bold text-gray-900">
                    {programs.reduce((acc, p) => acc + (p.currentEnrollment || 0), 0)}
                  </p>
                  <p className="text-xs text-gray-600">students</p>
                </div>
                
                <div className="bg-white rounded-2xl border border-gray-200 p-3 md:p-4 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xs md:text-sm font-medium text-gray-500">Featured</h3>
                    <FaStar className="w-3 h-3 md:w-4 md:h-4 text-yellow-500" />
                  </div>
                  <p className="text-xl md:text-2xl font-bold text-gray-900">
                    {programs.filter(p => p.isFeatured && !p.isArchived).length}
                  </p>
                  <p className="text-xs text-gray-600">programs</p>
                </div>
                
                <div className="bg-white rounded-2xl border border-gray-200 p-3 md:p-4 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xs md:text-sm font-medium text-gray-500">Avg Fill Rate</h3>
                    <FaChartBar className="w-3 h-3 md:w-4 md:h-4 text-purple-500" />
                  </div>
                  <p className="text-xl md:text-2xl font-bold text-gray-900">
                    {programs.length > 0 ? 
                      Math.round(programs.reduce((acc, p) => acc + ((p.currentEnrollment || 0) / (p.capacity || 1) * 100), 0) / programs.length) 
                      : 0}%
                  </p>
                  <p className="text-xs text-gray-600">capacity used</p>
                </div>
              </div>
          
              {/* Programs List with Enrolled Members */}
              <div className="space-y-4">
                {programs
                  .filter(program => showArchivedPrograms ? program.isArchived : !program.isArchived)
                  .map((program) => (
                  <div key={program._id} className="bg-white rounded-2xl border border-gray-200 p-4 md:p-6 shadow-sm">
                    {/* Program Header */}
                    <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4 mb-4">
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <div className={`p-2 rounded-lg ${
                            program.category === 'core' ? 'bg-green-100' :
                            program.category === 'academic' ? 'bg-blue-100' :
                            program.category === 'cultural' ? 'bg-yellow-100' :
                            program.category === 'creative' ? 'bg-pink-100' :
                            program.category === 'premium' ? 'bg-red-100' :
                            'bg-gray-100'
                          }`}>
                            {program.icon === 'FaCrown' && <FaCrown className="w-4 h-4 md:w-5 md:h-5 text-red-600" />}
                            {program.icon === 'FaMicrophone' && <FaMicrophone className="w-4 h-4 md:w-5 md:h-5 text-blue-600" />}
                            {program.icon === 'FaUsers' && <FaUsers className="w-4 h-4 md:w-5 md:h-5 text-green-600" />}
                            {program.icon === 'FaSpellCheck' && <FaSpellCheck className="w-4 h-4 md:w-5 md:h-5 text-purple-600" />}
                            {program.icon === 'FaBookOpen' && <FaBookOpen className="w-4 h-4 md:w-5 md:h-5 text-yellow-600" />}
                            {program.icon === 'FaPalette' && <FaPalette className="w-4 h-4 md:w-5 md:h-5 text-pink-600" />}
                            {program.icon === 'FaFootballBall' && <FaFootballBall className="w-4 h-4 md:w-5 md:h-5 text-orange-600" />}
                            {!['FaCrown', 'FaMicrophone', 'FaUsers', 'FaSpellCheck', 'FaBookOpen', 'FaPalette', 'FaFootballBall'].includes(program.icon) && (
                              <FaGraduationCap className="w-4 h-4 md:w-5 md:h-5 text-gray-600" />
                            )}
                          </div>
                          
                          <h3 className="text-base md:text-xl font-semibold text-gray-900">{program.title}</h3>
                          
                          {program.isFeatured && (
                            <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
                              ⭐ Featured
                            </span>
                          )}
                          
                          <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
                            program.category === 'core' ? 'bg-green-100 text-green-800' :
                            program.category === 'academic' ? 'bg-blue-100 text-blue-800' :
                            program.category === 'cultural' ? 'bg-yellow-100 text-yellow-800' :
                            program.category === 'creative' ? 'bg-pink-100 text-pink-800' :
                            program.category === 'premium' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {program.category}
                          </span>
                          
                          {program.isArchived && (
                            <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                              Archived
                            </span>
                          )}
                        </div>
                        
                        <p className="text-gray-600 mb-3 text-sm md:text-base">{program.description}</p>
                        
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 md:gap-4 text-xs md:text-sm">
                          <div>
                            <span className="text-gray-500">Duration:</span>
                            <p className="font-medium truncate">{program.duration}</p>
                          </div>
                          <div>
                            <span className="text-gray-500">Capacity:</span>
                            <p className="font-medium">{program.capacity} students</p>
                          </div>
                          <div>
                            <span className="text-gray-500">Badge:</span>
                            <p className="font-medium truncate">{program.badge}</p>
                          </div>
                          <div>
                            <span className="text-gray-500">Price:</span>
                            <p className="font-medium truncate">
                              {program.price?.amount > 0 
                                ? `${program.price.currency} ${program.price.amount.toLocaleString()}`
                                : 'Free'
                              }
                            </p>
                          </div>
                          <div>
                            <span className="text-gray-500">Status:</span>
                            <p className={`font-medium capitalize truncate ${
                              program.enrollmentStatus === 'open' ? 'text-green-600' :
                              program.enrollmentStatus === 'closed' ? 'text-red-600' :
                              'text-yellow-600'
                            }`}>
                              {program.enrollmentStatus}
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex md:flex-col gap-2 md:ml-4">
                        <button 
                          onClick={() => handleViewProgram(program)}
                          className="flex-1 md:flex-none px-3 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-sm"
                        >
                          <FaEye className="w-4 h-4 mx-auto" />
                        </button>
                        <button 
                          onClick={() => handleEditProgram(program)}
                          className="flex-1 md:flex-none px-3 py-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors text-sm"
                        >
                          <FaEdit className="w-4 h-4 mx-auto" />
                        </button>
                        <button 
                          onClick={() => handleArchiveProgram(program._id)}
                          className={`flex-1 md:flex-none px-3 py-2 rounded-lg transition-colors text-sm ${
                            program.isArchived 
                              ? 'bg-green-50 text-green-600 hover:bg-green-100' 
                              : 'bg-yellow-50 text-yellow-600 hover:bg-yellow-100'
                          }`}
                          title={program.isArchived ? 'Unarchive program' : 'Archive program'}
                        >
                          <FaArchive className="w-4 h-4 mx-auto" />
                        </button>
                        <button 
                          onClick={() => handleDeleteProgram(program._id)}
                          className="flex-1 md:flex-none px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors text-sm"
                        >
                          <FaTrash className="w-4 h-4 mx-auto" />
                        </button>
                      </div>
                    </div>
          
                    {/* Enrolled Students Section */}
                    <div className="border-t border-gray-200 pt-4">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                        <h4 className="text-base md:text-lg font-semibold text-gray-900">
                          Enrolled Students ({program.currentEnrollment || 0}/{program.capacity})
                        </h4>
                        <div className="flex items-center space-x-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            (program.currentEnrollment || 0) >= program.capacity ? 'bg-red-100 text-red-800' :
                            (program.currentEnrollment || 0) > program.capacity * 0.8 ? 'bg-yellow-100 text-yellow-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {(program.currentEnrollment || 0) >= program.capacity ? 'Full' :
                             (program.currentEnrollment || 0) > program.capacity * 0.8 ? 'Almost Full' :
                             'Available'}
                          </span>
                          <div className="w-20 bg-gray-200 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full ${
                                (program.currentEnrollment || 0) >= program.capacity ? 'bg-red-500' :
                                (program.currentEnrollment || 0) > program.capacity * 0.8 ? 'bg-yellow-500' :
                                'bg-green-500'
                              }`}
                              style={{ 
                                width: `${Math.min(((program.currentEnrollment || 0) / program.capacity) * 100, 100)}%` 
                              }}
                            />
                          </div>
                        </div>
                      </div>
          
                      {/* Desktop Table - Enrolled Students */}
                      {program.enrolledUsers && program.enrolledUsers.length > 0 ? (
                        <>
                          <div className="hidden md:block overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Student</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Payment</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Enrolled</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200">
                                {program.enrolledUsers
                                  .filter(enrollment => enrollment.userId)
                                  .map((enrollment, index) => (
                                    <tr key={enrollment._id || index} className="hover:bg-gray-50">
                                      <td className="px-4 py-3">
                                        <div className="flex items-center space-x-2">
                                          <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-blue-600 rounded-full flex items-center justify-center">
                                            <span className="text-white text-xs font-semibold">
                                              {(() => {
                                                const name = enrollment.userId?.name || 
                                                           enrollment.userId?.teamName || 
                                                           enrollment.userId?.schoolName ||
                                                           enrollment.userName ||
                                                           'Unknown User';
                                                return name[0]?.toUpperCase() || 'U';
                                              })()}
                                            </span>
                                          </div>
                                          <div>
                                            <p className="font-medium text-gray-900">
                                              {enrollment.userId?.name || 
                                               enrollment.userId?.teamName || 
                                               enrollment.userId?.schoolName ||
                                               enrollment.userName ||
                                               'Unknown User'}
                                            </p>
                                          </div>
                                        </div>
                                      </td>
                                      <td className="px-4 py-3 text-gray-600">
                                        {enrollment.userId?.email || enrollment.userEmail || 'No email'}
                                      </td>
                                      <td className="px-4 py-3 text-gray-600">
                                        {enrollment.userId?.phone || 'No phone'}
                                      </td>
                                      <td className="px-4 py-3">
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                          enrollment.status === 'enrolled' ? 'bg-green-100 text-green-800' :
                                          enrollment.status === 'waitlist' ? 'bg-yellow-100 text-yellow-800' :
                                          enrollment.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                                          'bg-gray-100 text-gray-800'
                                        }`}>
                                          {enrollment.status}
                                        </span>
                                      </td>
                                      <td className="px-4 py-3">
                                        <ProgramPaymentBadge 
                                          enrollment={enrollment}
                                          programId={program._id}
                                          onUpdate={(updatedEnrollment) => {
                                            setPrograms(programs.map(p => {
                                              if (p._id === program._id) {
                                                return {
                                                  ...p,
                                                  enrolledUsers: p.enrolledUsers.map(e => 
                                                    e._id === updatedEnrollment._id ? updatedEnrollment : e
                                                  )
                                                };
                                              }
                                              return p;
                                            }));
                                          }}
                                        />
                                      </td>
                                      <td className="px-4 py-3 text-gray-600 text-xs">
                                        {formatRelativeDate(enrollment.enrolledAt || enrollment.userId?.createdAt || Date.now())}
                                      </td>

                                      {/* Actions Cell with Remove Button */}
                                      <td className="px-4 py-3">
                                        <button
                                          onClick={() => {
                                            const userName = enrollment.userId?.name ||
                                                           enrollment.userId?.teamName ||
                                                           enrollment.userId?.schoolName ||
                                                           'this user';
                                            if (window.confirm(`Remove ${userName} from the program?`)) {
                                              handleUnenrollFromProgram(program._id, enrollment.userId._id);
                                            }
                                          }}
                                          className="px-2 py-1 bg-red-50 text-red-600 rounded hover:bg-red-100 transition-colors text-xs flex items-center space-x-1"
                                          title="Remove user from program"
                                        >
                                          <FaTimes className="w-3 h-3" />
                                          <span>Remove</span>
                                        </button>
                                      </td>
                                    </tr>
                                  ))}
                              </tbody>
                            </table>
                          </div>
          
                          {/* Mobile Cards - Enrolled Students */}
                          <div className="md:hidden space-y-3">
                            {program.enrolledUsers
                              .filter(enrollment => enrollment.userId)
                              .map((enrollment, index) => (
                                <div key={enrollment._id || index} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                                  <div className="flex items-start space-x-3 mb-3">
                                    <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                                      <span className="text-white text-sm font-semibold">
                                        {(() => {
                                          const name = enrollment.userId?.name || 
                                                     enrollment.userId?.teamName || 
                                                     enrollment.userId?.schoolName ||
                                                     enrollment.userName ||
                                                     'Unknown User';
                                          return name[0]?.toUpperCase() || 'U';
                                        })()}
                                      </span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="font-semibold text-gray-900 text-sm truncate">
                                        {enrollment.userId?.name || 
                                         enrollment.userId?.teamName || 
                                         enrollment.userId?.schoolName ||
                                         enrollment.userName ||
                                         'Unknown User'}
                                      </p>
                                      <p className="text-xs text-gray-600 truncate">
                                        {enrollment.userId?.email || enrollment.userEmail || 'No email'}
                                      </p>
                                    </div>
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                                      enrollment.status === 'enrolled' ? 'bg-green-100 text-green-800' :
                                      enrollment.status === 'waitlist' ? 'bg-yellow-100 text-yellow-800' :
                                      enrollment.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                                      'bg-gray-100 text-gray-800'
                                    }`}>
                                      {enrollment.status}
                                    </span>
                                  </div>
                                  
                                  <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                                    <div>
                                      <span className="text-gray-500">Phone:</span>
                                      <p className="font-medium truncate">{enrollment.userId?.phone || 'N/A'}</p>
                                    </div>
                                    <div>
                                      <span className="text-gray-500">Enrolled:</span>
                                      <p className="font-medium">
                                        {formatRelativeDate(enrollment.enrolledAt || enrollment.userId?.createdAt || Date.now())}
                                      </p>
                                    </div>
                                  </div>
          
                                  <div className="border-t border-gray-200 pt-2">
                                    <ProgramPaymentBadge
                                      enrollment={enrollment}
                                      programId={program._id}
                                      mobile={true}
                                      onUpdate={(updatedEnrollment) => {
                                        setPrograms(programs.map(p => {
                                          if (p._id === program._id) {
                                            return {
                                              ...p,
                                              enrolledUsers: p.enrolledUsers.map(e =>
                                                e._id === updatedEnrollment._id ? updatedEnrollment : e
                                              )
                                            };
                                          }
                                          return p;
                                        }));
                                      }}
                                    />
                                  </div>

                                  {/* Remove Button for Mobile */}
                                  <div className="border-t border-gray-200 pt-2 mt-2">
                                    <button
                                      onClick={() => {
                                        const userName = enrollment.userId?.name ||
                                                       enrollment.userId?.teamName ||
                                                       enrollment.userId?.schoolName ||
                                                       'this user';
                                        if (window.confirm(`Remove ${userName} from the program?`)) {
                                          handleUnenrollFromProgram(program._id, enrollment.userId._id);
                                        }
                                      }}
                                      className="w-full px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors text-xs font-medium flex items-center justify-center space-x-1"
                                    >
                                      <FaTimes className="w-3 h-3" />
                                      <span>Remove from Program</span>
                                    </button>
                                  </div>
                                </div>
                              ))}
                          </div>
                        </>
                      ) : (
                        <div className="text-center py-8">
                          <FaUsers className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                          <h4 className="text-sm font-medium text-gray-900 mb-1">No Students Enrolled</h4>
                          <p className="text-gray-600 text-xs">Students will appear here as they enroll</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                
                {programs.filter(program => showArchivedPrograms ? program.isArchived : !program.isArchived).length === 0 && (
                  <div className="text-center py-12">
                    <FaGraduationCap className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {showArchivedPrograms ? 'No Archived Programs' : 'No Programs Found'}
                    </h3>
                    <p className="text-gray-600 mb-4">
                      {showArchivedPrograms 
                        ? 'No programs have been archived yet' 
                        : 'Create your first program to get started'}
                    </p>
                    {!showArchivedPrograms && (
                      <button
                        onClick={() => setShowProgramModal(true)}
                        className="px-6 py-2 bg-green-500 text-white rounded-2xl hover:bg-green-600 transition-colors"
                      >
                        Create First Program
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* USERS TAB */}
          {!loading && activeTab === 'users' && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <h2 className="text-2xl font-bold text-gray-900">User Management</h2>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={exportUsers}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-2xl hover:bg-blue-600 transition-colors"
                  >
                    <FaDownload className="w-4 h-4" />
                    <span>Export Users</span>
                  </button>
                </div>
              </div>
          
              {/* User Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium text-gray-500">Total Users</h3>
                    <FaUsers className="w-4 h-4 text-blue-500" />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{users.length}</p>
                </div>
                
                <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium text-gray-500">Active Users</h3>
                    <FaCheck className="w-4 h-4 text-green-500" />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">
                    {users.filter(u => u.status === 'active').length}
                  </p>
                </div>
                
                <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium text-gray-500">Pending Approval</h3>
                    <FaExclamationTriangle className="w-4 h-4 text-yellow-500" />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">
                    {users.filter(u => u.status === 'pending').length}
                  </p>
                </div>
                
                <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium text-gray-500">This Month</h3>
                    <FaUsers className="w-4 h-4 text-purple-500" />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">
                    {users.filter(u => new Date(u.createdAt) > new Date(Date.now() - 30*24*60*60*1000)).length}
                  </p>
                </div>
              </div>
          
              {/* Users List */}
              <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">All Users ({users.length})</h3>
                
                {users.length > 0 ? (
                  <>
                    {/* Desktop Table */}
                    <div className="hidden md:block overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Joined</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {users.map((user) => (
                            <tr key={user._id} className="hover:bg-gray-50">
                              <td className="px-4 py-3">
                                <div className="flex items-center space-x-2">
                                  <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                                    <span className="text-white text-xs font-semibold">
                                      {(user.name || user.teamName || user.schoolName || 'U')[0].toUpperCase()}
                                    </span>
                                  </div>
                                  <div>
                                    <p className="font-medium text-gray-900">
                                      {user.name || user.teamName || user.schoolName || 'Unknown User'}
                                    </p>
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-gray-600">{user.email || 'No email'}</td>
                              <td className="px-4 py-3 text-gray-600">{user.phone || 'No phone'}</td>
                              <td className="px-4 py-3">
                                <span className="capitalize text-gray-900">
                                  {user.registrationType || 'Unknown'}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  user.status === 'active' ? 'bg-green-100 text-green-800' :
                                  user.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-red-100 text-red-800'
                                }`}>
                                  {user.status || 'Unknown'}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-gray-600 text-xs">
                                {formatRelativeDate(user.createdAt)}
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex items-center space-x-2">
                                  <button
                                    onClick={() => handleViewUser(user)}
                                    className="px-2 py-1 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition-colors text-xs"
                                    title="View Details"
                                  >
                                    <FaEye className="w-3 h-3" />
                                  </button>
                                  {user.status === 'pending' && (
                                    <button
                                      onClick={() => handleApproveUser(user._id)}
                                      className="px-2 py-1 bg-green-50 text-green-600 rounded hover:bg-green-100 transition-colors text-xs"
                                      title="Approve User"
                                    >
                                      <FaCheck className="w-3 h-3" />
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleDeleteUser(user._id)}
                                    className="px-2 py-1 bg-red-50 text-red-600 rounded hover:bg-red-100 transition-colors text-xs"
                                    title="Delete User"
                                  >
                                    <FaTrash className="w-3 h-3" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
          
                    {/* Mobile Cards */}
                    <div className="md:hidden space-y-3">
                      {users.map((user) => (
                        <div key={user._id} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                          <div className="flex items-start space-x-3 mb-3">
                            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                              <span className="text-white text-sm font-semibold">
                                {(user.name || user.teamName || user.schoolName || 'U')[0].toUpperCase()}
                              </span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-gray-900 text-sm truncate">
                                {user.name || user.teamName || user.schoolName || 'Unknown User'}
                              </p>
                              <p className="text-xs text-gray-600 truncate">{user.email || 'No email'}</p>
                            </div>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                              user.status === 'active' ? 'bg-green-100 text-green-800' :
                              user.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {user.status}
                            </span>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                            <div>
                              <span className="text-gray-500">Phone:</span>
                              <p className="font-medium truncate">{user.phone || 'N/A'}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Type:</span>
                              <p className="font-medium capitalize">{user.registrationType || 'Unknown'}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Joined:</span>
                              <p className="font-medium">{formatRelativeDate(user.createdAt)}</p>
                            </div>
                          </div>
          
                          <div className="flex space-x-2 pt-2 border-t border-gray-200">
                            <button
                              onClick={() => handleViewUser(user)}
                              className="flex-1 px-3 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-xs"
                            >
                              View
                            </button>
                            {user.status === 'pending' && (
                              <button
                                onClick={() => handleApproveUser(user._id)}
                                className="flex-1 px-3 py-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors text-xs"
                              >
                                Approve
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteUser(user._id)}
                              className="flex-1 px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors text-xs"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-12">
                    <FaUsers className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No Users Found</h3>
                    <p className="text-gray-600">Users will appear here as they register</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* NOTIFICATIONS TAB */}
          {!loading && activeTab === 'notifications' && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <h2 className="text-2xl font-bold text-gray-900">Notification Management</h2>
                <button
                  onClick={() => setShowNotificationModal(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-2xl hover:bg-blue-600 transition-colors"
                >
                  <FaBell className="w-4 h-4" />
                  <span>Send Notification</span>
                </button>
              </div>

              <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-sm">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Sent Notifications ({notifications.length})</h3>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {notifications.map((notification) => (
                    <div key={notification._id} className="p-3 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-1">
                            <h4 className="font-medium text-gray-900 text-sm truncate">{notification.title}</h4>
                            <span className={`px-1.5 py-0.5 rounded-full text-xs font-medium ${
                              notification.type === 'success' ? 'bg-green-100 text-green-800' :
                              notification.type === 'warning' ? 'bg-yellow-100 text-yellow-800' :
                              notification.type === 'error' ? 'bg-red-100 text-red-800' :
                              'bg-blue-100 text-blue-800'
                            }`}>
                              {notification.type}
                            </span>
                          </div>
                          <p className="text-gray-600 text-xs mb-1 line-clamp-2">{notification.message}</p>
                          <p className="text-gray-500 text-xs">
                            To: {notification.targetUsers} • {new Date(notification.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                        <button 
                          onClick={() => {
                            setNotifications(notifications.filter(n => n._id !== notification._id));
                            toast.success('Notification deleted');
                          }}
                          className="px-2 py-1 bg-red-50 text-red-600 rounded-lg text-xs hover:bg-red-100 transition-colors ml-2"
                        >
                          <FaTrash className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                  
                  {notifications.length === 0 && (
                    <div className="text-center py-6">
                      <FaBell className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <h4 className="font-medium text-gray-900 mb-1 text-sm">No Notifications Sent</h4>
                      <p className="text-gray-600 text-xs">Send your first notification to users</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          
          {/* WINNERS TAB - FIXED VERSION */}
          {!loading && activeTab === 'winners' && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <h2 className="text-2xl font-bold text-gray-900">Winners Management</h2>
                <button
                  onClick={() => setShowWinnerModal(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-yellow-500 text-white rounded-2xl hover:bg-yellow-600 transition-colors"
                >
                  <FaTrophy className="w-4 h-4" />
                  <span>Update Winners</span>
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {winners.map((winner, index) => {
                  // Find the associated event
                  const associatedEvent = events.find(e => e._id === winner.eventId);
                  
                  return (
                    <div key={winner._id || index} className="bg-white rounded-3xl border border-gray-200 p-6 shadow-sm">
                      <div className="flex items-center space-x-3 mb-4">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                          winner.position === '1st' ? 'bg-gradient-to-br from-yellow-400 to-yellow-600' :
                          winner.position === '2nd' ? 'bg-gradient-to-br from-gray-400 to-gray-600' :
                          'bg-gradient-to-br from-amber-600 to-amber-800'
                        }`}>
                          {winner.position === '1st' ? <FaCrown className="w-6 h-6 text-white" /> :
                           winner.position === '2nd' ? <FaMedal className="w-6 h-6 text-white" /> :
                           <FaAward className="w-6 h-6 text-white" />}
                        </div>
                        <div>
                          <h3 className="font-bold text-gray-900">{winner.position} Place</h3>
                          <p className="text-sm text-gray-600">{winner.eventTitle || 'Unknown Event'}</p>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <p className="font-semibold text-gray-900">{winner.winnerName || 'No name provided'}</p>
                        <p className="text-sm text-gray-600">Prize: {winner.prize || 'No prize specified'}</p>
                        
                        {/* Fixed date display */}
                        <div className="text-xs text-gray-500 space-y-1">
                          {associatedEvent ? (
                            <>
                              <p>Event Date: {formatDate(associatedEvent.date)}</p>
                              <p>Winner Announced: {formatRelativeDate(winner.createdAt || winner.updatedAt)}</p>
                            </>
                          ) : (
                            <>
                              <p>Event Date: {winner.eventDate ? formatDate(winner.eventDate) : 'Not specified'}</p>
                              <p>Announced: {formatRelativeDate(winner.createdAt || winner.updatedAt || Date.now())}</p>
                            </>
                          )}
                          
                          {/* Debug info (remove in production) */}
                          <details className="mt-2">
                            <summary className="cursor-pointer text-xs text-blue-600">Debug Info</summary>
                            <div className="mt-1 p-2 bg-gray-100 rounded text-xs">
                              <p>Winner ID: {winner._id}</p>
                              <p>Event ID: {winner.eventId}</p>
                              <p>Raw createdAt: {JSON.stringify(winner.createdAt)}</p>
                              <p>Associated Event: {associatedEvent ? 'Found' : 'Not found'}</p>
                              {associatedEvent && <p>Event Raw Date: {JSON.stringify(associatedEvent.date)}</p>}
                            </div>
                          </details>
                        </div>
                      </div>
                    </div>
                  );
                })}
                
                {winners.length === 0 && (
                  <div className="col-span-full text-center py-12">
                    <FaTrophy className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No Winners Declared</h3>
                    <p className="text-gray-600 mb-4">Declare winners for completed events</p>
                    <button
                      onClick={() => setShowWinnerModal(true)}
                      className="px-6 py-2 bg-yellow-500 text-white rounded-2xl hover:bg-yellow-600 transition-colors"
                    >
                      Update Winners
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* GALLERY TAB */}
          {!loading && activeTab === 'gallery' && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <h2 className="text-2xl font-bold text-gray-900">Gallery Management</h2>
                <button
                  onClick={() => setShowGalleryModal(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-purple-500 text-white rounded-2xl hover:bg-purple-600 transition-colors"
                >
                  <FaUpload className="w-4 h-4" />
                  <span>Upload Image</span>
                </button>
              </div>
          
              {/* Gallery Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-sm">
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Total Images</h3>
                  <p className="text-2xl font-bold text-gray-900">{galleryItems.length}</p>
                </div>
                {['competitions', 'awards', 'moments', 'events'].map(cat => (
                  <div key={cat} className="bg-white rounded-2xl border border-gray-200 p-4 shadow-sm">
                    <h3 className="text-sm font-medium text-gray-500 mb-2 capitalize">{cat}</h3>
                    <p className="text-2xl font-bold text-gray-900">
                      {galleryItems.filter(item => item.category === cat).length}
                    </p>
                  </div>
                ))}
              </div>
          
              {/* Gallery Grid */}
              <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
                {galleryItems.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {galleryItems.map((item) => (
                      <div key={item._id} className="group relative">
                        <div className="aspect-square overflow-hidden rounded-xl border border-gray-200">
                          <img
                            src={item.imageUrl}
                            alt={item.title}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                          />
                        </div>
                        
                        {/* Overlay */}
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex flex-col items-center justify-center p-3">
                          <p className="text-white text-sm font-semibold mb-1 text-center line-clamp-2">
                            {item.title || 'Untitled'}
                          </p>
                          <span className="text-xs text-white/80 mb-3 capitalize">{item.category}</span>
                          <button
                            onClick={() => handleDeleteGalleryImage(item._id)}
                            className="px-3 py-1.5 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-xs"
                          >
                            <FaTrash className="inline mr-1" />
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <FaImages className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No Images Yet</h3>
                    <p className="text-gray-600 mb-4">Upload your first gallery image</p>
                    <button
                      onClick={() => setShowGalleryModal(true)}
                      className="px-6 py-2 bg-purple-500 text-white rounded-2xl hover:bg-purple-600 transition-colors"
                    >
                      Upload Image
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ANALYTICS TAB */}
          {!loading && activeTab === 'analytics' && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <h2 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h2>
                <button 
                  onClick={() => setShowAdvancedAnalytics(true)}
                  className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-2xl hover:from-purple-600 hover:to-blue-600 transition-all duration-300 shadow-lg transform hover:scale-105"
                >
                  <FaCube className="w-5 h-5" />
                  <span className="font-semibold">Advanced Analytics</span>
                </button>
              </div>

              {/* Analytics Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white rounded-3xl border border-gray-200 p-6 shadow-sm">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">User Growth</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">This Month</span>
                      <span className="font-semibold text-green-600">
                        +{users.filter(u => new Date(u.createdAt) > new Date(Date.now() - 30*24*60*60*1000)).length}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Total Active</span>
                      <span className="font-semibold text-blue-600">
                        {users.filter(u => u.status === 'active').length}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Conversion Rate</span>
                      <span className="font-semibold text-purple-600">
                        {users.length > 0 ? Math.round((users.filter(u => u.status === 'active').length / users.length) * 100) : 0}%
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-3xl border border-gray-200 p-6 shadow-sm">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Event Performance</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Upcoming Events</span>
                      <span className="font-semibold text-green-600">
                        {events.filter(e => new Date(e.date) > new Date()).length}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Avg Participants</span>
                      <span className="font-semibold text-blue-600">
                        {events.length > 0 ? Math.round(events.reduce((acc, e) => acc + (e.participants?.length || 0), 0) / events.length) : 0}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Fill Rate</span>
                      <span className="font-semibold text-purple-600">
                        {events.length > 0 ? Math.round((events.reduce((acc, e) => acc + (e.participants?.length || 0), 0) / events.reduce((acc, e) => acc + e.maxParticipants, 0)) * 100) : 0}%
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-3xl border border-gray-200 p-6 shadow-sm">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Registration Types</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Schools</span>
                      <span className="font-semibold text-green-600">
                        {users.filter(u => u.registrationType === 'school').length}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Teams</span>
                      <span className="font-semibold text-blue-600">
                        {users.filter(u => u.registrationType === 'team').length}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Individuals</span>
                      <span className="font-semibold text-purple-600">
                        {users.filter(u => u.registrationType === 'individual').length}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-3xl border border-gray-200 p-6 shadow-sm">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Live Sessions Stats</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Total Sessions</span>
                      <span className="font-semibold text-green-600">
                        {liveSessions.length}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Active Now</span>
                      <span className="font-semibold text-red-600">
                        {liveSessions.filter(s => s.isLive).length}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Avg Participants</span>
                      <span className="font-semibold text-purple-600">
                        {liveSessions.length > 0 ? Math.round(liveSessions.reduce((acc, s) => acc + (s.participants?.length || 0), 0) / liveSessions.length) : 0}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Export Section */}
              <div className="bg-white rounded-3xl border border-gray-200 p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Data Export</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <button className="flex items-center justify-center space-x-2 p-3 bg-green-50 border border-green-200 rounded-2xl hover:bg-green-100 transition-colors">
                    <FaDownload className="w-4 h-4 text-green-600" />
                    <span className="text-green-700 font-medium">Export Users</span>
                  </button>
                  <button className="flex items-center justify-center space-x-2 p-3 bg-blue-50 border border-blue-200 rounded-2xl hover:bg-blue-100 transition-colors">
                    <FaDownload className="w-4 h-4 text-blue-600" />
                    <span className="text-blue-700 font-medium">Export Events</span>
                  </button>
                  <button className="flex items-center justify-center space-x-2 p-3 bg-purple-50 border border-purple-200 rounded-2xl hover:bg-purple-100 transition-colors">
                    <FaDownload className="w-4 h-4 text-purple-600" />
                    <span className="text-purple-700 font-medium">Export Analytics</span>
                  </button>
                  <button className="flex items-center justify-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-2xl hover:bg-red-100 transition-colors">
                    <FaDownload className="w-4 h-4 text-red-600" />
                    <span className="text-red-700 font-medium">Export Sessions</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* SETTINGS TAB */}
          {!loading && activeTab === 'settings' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-900">System Settings</h2>

              {/* Application Settings */}
              <div className="bg-white rounded-3xl border border-gray-200 p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Application Settings</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl">
                    <div>
                      <h4 className="font-medium text-gray-900">Email Notifications</h4>
                      <p className="text-sm text-gray-600">Send automated email notifications to users</p>
                    </div>
                    <button 
                      onClick={() => handleSettingToggle('emailNotifications')}
                      className="relative"
                    >
                      {settings.emailNotifications ? (
                        <FaToggleOn className="w-8 h-8 text-green-500" />
                      ) : (
                        <FaToggleOff className="w-8 h-8 text-gray-400" />
                      )}
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl">
                    <div>
                      <h4 className="font-medium text-gray-900">Auto Approval</h4>
                      <p className="text-sm text-gray-600">Automatically approve new user registrations</p>
                    </div>
                    <button 
                      onClick={() => handleSettingToggle('autoApproval')}
                      disabled={settingsLoading}
                      className="relative"
                    >
                      {settings.autoApproval ? (
                        <FaToggleOn className={`w-8 h-8 ${settingsLoading ? 'text-gray-400' : 'text-green-500'}`} />
                      ) : (
                        <FaToggleOff className={`w-8 h-8 ${settingsLoading ? 'text-gray-300' : 'text-gray-400'}`} />
                      )}
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl">
                    <div>
                      <h4 className="font-medium text-gray-900">Registration Open</h4>
                      <p className="text-sm text-gray-600">Allow new user registrations</p>
                    </div>
                    <button 
                      onClick={() => handleSettingToggle('registrationOpen')}
                      className="relative"
                    >
                      {settings.registrationOpen ? (
                        <FaToggleOn className="w-8 h-8 text-green-500" />
                      ) : (
                        <FaToggleOff className="w-8 h-8 text-gray-400" />
                      )}
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-red-50 rounded-2xl border border-red-200">
                    <div>
                      <h4 className="font-medium text-red-900">Maintenance Mode</h4>
                      <p className="text-sm text-red-600">Put the application in maintenance mode</p>
                    </div>
                    <button 
                      onClick={() => handleSettingToggle('maintenanceMode')}
                      className="relative"
                    >
                      {settings.maintenanceMode ? (
                        <FaToggleOn className="w-8 h-8 text-red-500" />
                      ) : (
                        <FaToggleOff className="w-8 h-8 text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* System Information */}
              <div className="bg-white rounded-3xl border border-gray-200 p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">System Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Application Version</h4>
                    <p className="text-gray-600">v1.0.0</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Last Backup</h4>
                    <p className="text-gray-600">{new Date().toLocaleDateString()}</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Server Status</h4>
                    <span className="px-2 py-1 bg-green-100 text-green-800 text-sm rounded-full">Online</span>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Database Status</h4>
                    <span className="px-2 py-1 bg-green-100 text-green-800 text-sm rounded-full">Connected</span>
                  </div>
                </div>
              </div>

              {/* Danger Zone */}
              <div className="bg-red-50 border border-red-200 rounded-3xl p-6">
                <h3 className="text-lg font-semibold text-red-900 mb-4">Danger Zone</h3>
                <div className="space-y-3">
                  <button className="w-full md:w-auto px-6 py-2 bg-red-500 text-white rounded-2xl hover:bg-red-600 transition-colors">
                    Clear All Data
                  </button>
                  <button className="w-full md:w-auto px-6 py-2 bg-red-600 text-white rounded-2xl hover:bg-red-700 transition-colors ml-0 md:ml-3">
                    Reset System
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* MODALS */}
      
      {/* Create Event Modal - FULLY UPDATED */}
      <AnimatePresence>
        {showEventModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowEventModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl max-w-4xl w-full max-h-[95vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 md:p-6">
                {/* Header */}
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-lg md:text-xl font-bold text-gray-900">
                    {editingEvent ? 'Edit Event' : 'Create Event'}
                  </h2>
                  <button
                    onClick={() => {
                      setShowEventModal(false);
                      setEditingEvent(null);
                      resetEventForm();
                    }}
                    className="text-gray-500 hover:text-gray-700 p-1 rounded-lg hover:bg-gray-100"
                  >
                    <FaTimes className="w-5 h-5" />
                  </button>
                </div>
      
                <form onSubmit={handleCreateEvent} className="space-y-4">
                  {/* Row 1: Basic Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Event Title *</label>
                      <input
                        type="text"
                        value={eventForm.title}
                        onChange={(e) => setEventForm({...eventForm, title: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="e.g., National Debate Championship 2025"
                        required
                      />
                    </div>
      
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Event Type *</label>
                      <select
                        value={eventForm.type}
                        onChange={(e) => setEventForm({...eventForm, type: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      >
                        <option value="tournament">Tournament</option>
                        <option value="workshop">Workshop</option>
                        <option value="training">Training</option>
                        <option value="cultural">Cultural Event</option>
                        <option value="conference">Conference</option>
                      </select>
                    </div>
      
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Time Zone *</label>
                      <select
                        value={eventForm.timezone}
                        onChange={(e) => setEventForm({...eventForm, timezone: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      >
                        <option value="EAT">EAT (East Africa Time)</option>
                        <option value="GMT">GMT</option>
                        <option value="WAT">WAT (West Africa Time)</option>
                        <option value="CAT">CAT (Central Africa Time)</option>
                        <option value="SAST">SAST (South Africa Time)</option>
                      </select>
                    </div>
                  </div>
      
                  {/* Row 2: Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Event Description *</label>
                    <textarea
                      value={eventForm.description}
                      onChange={(e) => setEventForm({...eventForm, description: e.target.value})}
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="Provide a detailed description of the event..."
                      required
                    />
                  </div>
      
                  {/* Row 3: Date & Time */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Start Date *</label>
                      <input
                        type="date"
                        value={eventForm.date}
                        onChange={(e) => setEventForm({...eventForm, date: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        End Date {eventForm.endDate && <span className="text-green-600">✓</span>}
                      </label>
                      <input
                        type="date"
                        value={eventForm.endDate}
                        onChange={(e) => setEventForm({...eventForm, endDate: e.target.value})}
                        min={eventForm.date}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="Optional - for multi-day events"
                      />
                    </div>
      
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Start Time *</label>
                      <input
                        type="time"
                        value={eventForm.time}
                        onChange={(e) => setEventForm({...eventForm, time: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        End Time {eventForm.endTime && <span className="text-green-600">✓</span>}
                      </label>
                      <input
                        type="time"
                        value={eventForm.endTime}
                        onChange={(e) => setEventForm({...eventForm, endTime: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="Optional"
                      />
                    </div>
                  </div>
      
                  {/* Row 4: Levels (Multiple Selection) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Eligible Levels * (Select all that apply)</label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {[
                        { value: 'juniors', label: 'Juniors (Grade 4-8)', desc: 'Primary/Elementary' },
                        { value: 'seniors', label: 'Seniors (Grade 9-12)', desc: 'High School/Form 1-4' },
                        { value: 'university', label: 'University/College', desc: 'Tertiary Education' },
                        { value: 'working', label: 'Working Class', desc: 'Professionals' },
                        { value: 'all', label: 'All Levels', desc: 'Everyone Welcome' }
                      ].map((level) => (
                        <label
                          key={level.value}
                          className={`flex items-start p-3 border-2 rounded-lg cursor-pointer transition-all ${
                            eventForm.levels.includes(level.value)
                              ? 'border-green-500 bg-green-50'
                              : 'border-gray-200 hover:border-green-300'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={eventForm.levels.includes(level.value)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setEventForm({...eventForm, levels: [...eventForm.levels, level.value]});
                              } else {
                                setEventForm({...eventForm, levels: eventForm.levels.filter(l => l !== level.value)});
                              }
                            }}
                            className="mt-1 w-4 h-4 text-green-600 rounded border-gray-300"
                          />
                          <div className="ml-3">
                            <p className="text-sm font-medium text-gray-900">{level.label}</p>
                            <p className="text-xs text-gray-500">{level.desc}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                    {eventForm.levels.length === 0 && (
                      <p className="text-xs text-red-600 mt-1">Please select at least one level</p>
                    )}
                  </div>
      
                  {/* Row 5: Location & Prize */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Location/Venue *</label>
                      <input
                        type="text"
                        value={eventForm.location}
                        onChange={(e) => setEventForm({...eventForm, location: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="e.g., Nairobi Convention Centre or Online via Zoom"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Prize/Awards</label>
                      <input
                        type="text"
                        value={eventForm.prize}
                        onChange={(e) => setEventForm({...eventForm, prize: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="e.g., KES 100,000, Trophies & Certificates"
                      />
                    </div>
                  </div>
      
                  {/* Row 6: Capacity & Registration Fee */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Max Individuals *</label>
                      <input
                        type="number"
                        value={eventForm.maxParticipants}
                        onChange={(e) => setEventForm({...eventForm, maxParticipants: parseInt(e.target.value) || 0})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                        min="1"
                        placeholder="50"
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">Total individual participants</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Max Institutions</label>
                      <input
                        type="number"
                        value={eventForm.maxInstitutions}
                        onChange={(e) => setEventForm({...eventForm, maxInstitutions: parseInt(e.target.value) || 0})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                        min="0"
                        placeholder="10"
                      />
                      <p className="text-xs text-gray-500 mt-1">Schools/Teams allowed</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Registration Fee</label>
                      <div className="flex gap-2">
                        <select
                          value={eventForm.registrationCurrency || 'KES'}
                          onChange={(e) => setEventForm({...eventForm, registrationCurrency: e.target.value})}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
                        >
                          <option value="KES">KES</option>
                          <option value="USD">USD</option>
                          <option value="EUR">EUR</option>
                          <option value="GBP">GBP</option>
                          <option value="UGX">UGX</option>
                          <option value="TZS">TZS</option>
                        </select>
                        <input
                          type="text"
                          value={eventForm.registrationFee}
                          onChange={(e) => setEventForm({...eventForm, registrationFee: e.target.value})}
                          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                          placeholder="e.g., 2,000 or Free"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Select currency and enter amount</p>
                    </div>
                  </div>
      
                  {/* Row 7: Thumbnail */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Event Poster/Thumbnail</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setEventForm({...eventForm, thumbnailFile: e.target.files[0]})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Recommended: 1200x630px (16:9 ratio) • Max 15MB • High quality images for better visibility
                    </p>
                    {eventForm.thumbnailFile && (
                      <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
                        <span className="text-sm text-green-700">✓ {eventForm.thumbnailFile.name}</span>
                        <button
                          type="button"
                          onClick={() => setEventForm({...eventForm, thumbnailFile: null})}
                          className="text-red-600 hover:text-red-800"
                        >
                          <FaTimes className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
      
                  {/* Row 8: Toggles */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                    {/* Payment Required Toggle */}
                    <label className="flex items-center justify-between cursor-pointer">
                      <div>
                        <span className="font-medium text-gray-900">Payment Required</span>
                        <p className="text-xs text-gray-600">Participants must pay registration fee</p>
                      </div>
                      <div className="relative">
                        <input
                          type="checkbox"
                          checked={eventForm.paymentRequired}
                          onChange={(e) => {
                            e.stopPropagation();
                            setEventForm({...eventForm, paymentRequired: e.target.checked});
                          }}
                          className="sr-only"
                        />
                        <div
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setEventForm({...eventForm, paymentRequired: !eventForm.paymentRequired});
                          }}
                          className={`w-12 h-6 rounded-full transition-colors cursor-pointer ${
                            eventForm.paymentRequired ? 'bg-green-500' : 'bg-gray-300'
                          }`}
                        >
                          <div
                            className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${
                              eventForm.paymentRequired ? 'translate-x-6' : 'translate-x-0.5'
                            } mt-0.5 pointer-events-none`}
                          />
                        </div>
                      </div>
                    </label>
      
                    {/* Featured Event Toggle */}
                    <label className="flex items-center justify-between cursor-pointer">
                      <div>
                        <span className="font-medium text-gray-900">Featured Event</span>
                        <p className="text-xs text-gray-600">Show prominently on homepage</p>
                      </div>
                      <div className="relative">
                        <input
                          type="checkbox"
                          checked={eventForm.featured}
                          onChange={(e) => {
                            e.stopPropagation();
                            setEventForm({...eventForm, featured: e.target.checked});
                          }}
                          className="sr-only"
                        />
                        <div
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setEventForm({...eventForm, featured: !eventForm.featured});
                          }}
                          className={`w-12 h-6 rounded-full transition-colors cursor-pointer ${
                            eventForm.featured ? 'bg-yellow-500' : 'bg-gray-300'
                          }`}
                        >
                          <div
                            className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${
                              eventForm.featured ? 'translate-x-6' : 'translate-x-0.5'
                            } mt-0.5 pointer-events-none`}
                          />
                        </div>
                      </div>
                    </label>
                  </div>
      
                  {/* Action Buttons */}
                  <div className="flex space-x-3 pt-4 border-t border-gray-200">
                    <button
                      type="submit"
                      disabled={loading || eventForm.levels.length === 0}
                      className="flex-1 bg-green-500 text-white py-3 px-4 rounded-xl hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 font-medium"
                    >
                      {loading ? (
                        <>
                          <FaSpinner className="w-5 h-5 animate-spin" />
                          <span>{editingEvent ? 'Updating...' : 'Creating...'}</span>
                        </>
                      ) : (
                        <>
                          <FaSave className="w-5 h-5" />
                          <span>{editingEvent ? 'Update Event' : 'Create Event'}</span>
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowEventModal(false);
                        setEditingEvent(null);
                        resetEventForm();
                      }}
                      className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* gallery modal */}
      {/* Upload Gallery Image Modal */}
      <AnimatePresence>
        {showGalleryModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowGalleryModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold text-gray-900">Upload Gallery Media</h2>
                  <button
                    onClick={() => setShowGalleryModal(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <FaTimes className="w-5 h-5" />
                  </button>
                </div>

                <form onSubmit={handleUploadGalleryImage} className="space-y-4">
                  {/* Media Type Selector */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Media Type *</label>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => setGalleryForm({...galleryForm, mediaType: 'image'})}
                        className={`flex-1 py-2 px-4 rounded-lg border-2 transition-all ${
                          galleryForm.mediaType === 'image'
                            ? 'border-purple-500 bg-purple-50 text-purple-700 font-medium'
                            : 'border-gray-300 text-gray-600 hover:border-gray-400'
                        }`}
                      >
                        <FaImages className="inline w-4 h-4 mr-2" />
                        Images
                      </button>
                      <button
                        type="button"
                        onClick={() => setGalleryForm({...galleryForm, mediaType: 'video'})}
                        className={`flex-1 py-2 px-4 rounded-lg border-2 transition-all ${
                          galleryForm.mediaType === 'video'
                            ? 'border-purple-500 bg-purple-50 text-purple-700 font-medium'
                            : 'border-gray-300 text-gray-600 hover:border-gray-400'
                        }`}
                      >
                        <FaVideo className="inline w-4 h-4 mr-2" />
                        Video
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Title (Optional)</label>
                    <input
                      type="text"
                      value={galleryForm.title}
                      onChange={(e) => setGalleryForm({...galleryForm, title: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      placeholder="Event name or description"
                    />
                  </div>

                  {galleryForm.mediaType === 'video' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Description (Optional)</label>
                      <textarea
                        value={galleryForm.description}
                        onChange={(e) => setGalleryForm({...galleryForm, description: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder="Video description"
                        rows={3}
                      />
                    </div>
                  )}
      
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Category *</label>
                    <select
                      value={galleryForm.category}
                      onChange={(e) => setGalleryForm({...galleryForm, category: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      required
                    >
                      <option value="competitions">Competitions</option>
                      <option value="awards">Awards</option>
                      <option value="moments">Moments</option>
                      <option value="events">Events</option>
                      <option value="officials">Officials</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
      
                  {/* Conditional File Input */}
                  {galleryForm.mediaType === 'image' ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Select Images * (Max 10)</label>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={(e) => {
                          const files = Array.from(e.target.files).slice(0, 10);
                          setGalleryForm({...galleryForm, imageFiles: files});
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        required
                      />
                      {galleryForm.imageFiles && galleryForm.imageFiles.length > 0 && (
                        <div className="mt-2 space-y-1">
                          <p className="text-sm font-medium text-green-600">
                            ✓ {galleryForm.imageFiles.length} image(s) selected
                          </p>
                          <div className="grid grid-cols-5 gap-2 mt-2">
                            {galleryForm.imageFiles.map((file, index) => (
                              <div key={index} className="relative group">
                                <img
                                  src={URL.createObjectURL(file)}
                                  alt={`Preview ${index + 1}`}
                                  className="w-full h-16 object-cover rounded border border-gray-300"
                                />
                                <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity rounded flex items-center justify-center">
                                  <span className="text-white text-xs">{index + 1}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      {/* Video File Upload */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Upload Video File</label>
                        <input
                          type="file"
                          accept="video/*"
                          onChange={(e) => {
                            setGalleryForm({...galleryForm, videoFile: e.target.files[0]});
                          }}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                        {galleryForm.videoFile && (
                          <p className="text-sm text-green-600 mt-2">
                            ✓ {galleryForm.videoFile.name}
                          </p>
                        )}
                      </div>

                      {/* Or Divider */}
                      <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                          <div className="w-full border-t border-gray-300"></div>
                        </div>
                        <div className="relative flex justify-center text-sm">
                          <span className="px-2 bg-white text-gray-500">OR</span>
                        </div>
                      </div>

                      {/* Video URL Input */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Video URL (YouTube, etc.)</label>
                        <input
                          type="url"
                          value={galleryForm.videoUrl}
                          onChange={(e) => setGalleryForm({...galleryForm, videoUrl: e.target.value})}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                          placeholder="https://youtube.com/watch?v=..."
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Provide either a video file or a URL (not both)
                        </p>
                      </div>
                    </>
                  )}
      
                  <div className="flex space-x-3 pt-4">
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 bg-purple-500 text-white py-3 px-4 rounded-xl hover:bg-purple-600 transition-colors disabled:opacity-50 flex items-center justify-center"
                    >
                      {loading ? (
                        <FaSpinner className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <FaUpload className="w-4 h-4 mr-2" />
                          Upload
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowGalleryModal(false)}
                      className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Send Notification Modal - Compact */}
      <AnimatePresence>
        {showNotificationModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowNotificationModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl max-w-sm w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-lg font-bold text-gray-900">Send Notification</h2>
                  <button
                    onClick={() => setShowNotificationModal(false)}
                    className="text-gray-500 hover:text-gray-700 p-1 rounded-lg hover:bg-gray-100"
                  >
                    <FaTimes className="w-4 h-4" />
                  </button>
                </div>

                <form onSubmit={handleSendNotification} className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Title</label>
                    <input
                      type="text"
                      value={notificationForm.title}
                      onChange={(e) => setNotificationForm({...notificationForm, title: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-green-500 text-sm"
                      placeholder="Notification title"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Message</label>
                    <textarea
                      value={notificationForm.message}
                      onChange={(e) => setNotificationForm({...notificationForm, message: e.target.value})}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-green-500 text-sm"
                      placeholder="Notification message"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
                      <select
                        value={notificationForm.type}
                        onChange={(e) => setNotificationForm({...notificationForm, type: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-green-500 text-sm"
                      >
                        <option value="info">Info</option>
                        <option value="success">Success</option>
                        <option value="warning">Warning</option>
                        <option value="error">Error</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Target</label>
                      <select
                        value={notificationForm.targetUsers}
                        onChange={(e) => setNotificationForm({...notificationForm, targetUsers: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-green-500 text-sm"
                      >
                        <option value="all">All Users</option>
                        <option value="pending">Pending</option>
                        <option value="active">Active</option>
                        <option value="schools">Schools</option>
                        <option value="teams">Teams</option>
                        <option value="individuals">Individuals</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex space-x-2 pt-3">
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 bg-blue-500 text-white py-2 px-3 rounded-[2rem] hover:bg-blue-600 transition-colors disabled:opacity-50 flex items-center justify-center space-x-1 text-sm"
                    >
                      {loading ? (
                        <FaSpinner className="w-3 h-3 animate-spin" />
                      ) : (
                        <>
                          <FaBell className="w-3 h-3" />
                          <span>Send</span>
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowNotificationModal(false)}
                      className="px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Update Winners Modal - Ultra Compact */}
      <AnimatePresence>
        {showWinnerModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowWinnerModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4">
                {/* Header */}
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    <FaTrophy className="w-4 h-4 text-yellow-500" />
                    Update Winners
                  </h2>
                  <button
                    onClick={() => setShowWinnerModal(false)}
                    className="text-gray-500 hover:text-gray-700 p-1 rounded-lg hover:bg-gray-100"
                  >
                    <FaTimes className="w-4 h-4" />
                  </button>
                </div>

                <form onSubmit={handleUpdateWinners} className="space-y-3">
                  {/* Event Selection */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Competition Event *</label>
                    <select
                      value={winnerForm.eventId}
                      onChange={(e) => setWinnerForm({...winnerForm, eventId: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-yellow-500 text-sm"
                      required
                    >
                      <option value="">Select completed event...</option>
                      {events
                        .filter(event => new Date(event.date) <= new Date())
                        .map((event) => (
                          <option key={event._id} value={event._id}>
                            {event.title} - {formatDate(event.date)}
                          </option>
                        ))}
                    </select>
                  </div>

                  {/* Winners Section */}
                  <div className="space-y-2">
                    <h3 className="text-sm font-semibold text-gray-900">Competition Results</h3>
                    {winnerForm.winners.map((winner, index) => (
                      <div key={index} className="p-3 border border-gray-200 rounded-lg space-y-2">
                        {/* Position Badge */}
                        <div className="flex items-center justify-between">
                          <div className={`text-xs font-bold px-3 py-1 rounded-full ${
                            winner.position === '1st' ? 'bg-yellow-100 text-yellow-800' :
                            winner.position === '2nd' ? 'bg-gray-100 text-gray-800' :
                            'bg-orange-100 text-orange-800'
                          }`}>
                            {winner.position === '1st' ? '🥇 1st Place' : 
                             winner.position === '2nd' ? '🥈 2nd Place' : 
                             '🥉 3rd Place'}
                          </div>
                        </div>
                        
                        {/* Winner Selection */}
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Winner *</label>
                          <select
                            value={winner.userId}
                            onChange={(e) => {
                              const newWinners = [...winnerForm.winners];
                              newWinners[index].userId = e.target.value;
                              setWinnerForm({...winnerForm, winners: newWinners});
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-yellow-500 text-sm"
                            required
                          >
                            <option value="">Select winner...</option>
                            {users
                              .filter(user => user.status === 'active')
                              .map((user) => (
                                <option key={user._id} value={user._id}>
                                  {user.name || user.teamName || user.schoolName}
                                </option>
                              ))}
                          </select>
                        </div>
                        
                        {/* Prize Input */}
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Prize/Reward *</label>
                          <input
                            type="text"
                            value={winner.prize}
                            onChange={(e) => {
                              const newWinners = [...winnerForm.winners];
                              newWinners[index].prize = e.target.value;
                              setWinnerForm({...winnerForm, winners: newWinners});
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-yellow-500 text-sm"
                            placeholder="KES 50,000, Gold Trophy, Certificate"
                            required
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Event Summary */}
                  {winnerForm.eventId && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <div className="flex items-center gap-2">
                        <FaCalendarAlt className="w-3 h-3 text-blue-600" />
                        <span className="text-xs font-medium text-blue-800">
                          {events.find(e => e._id === winnerForm.eventId)?.title}
                        </span>
                      </div>
                      <p className="text-xs text-blue-700 mt-1">
                        Event Date: {formatDate(events.find(e => e._id === winnerForm.eventId)?.date)}
                      </p>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex space-x-2 pt-3">
                    <button
                      type="submit"
                      disabled={loading || !winnerForm.eventId || !winnerForm.winners.some(w => w.userId && w.prize)}
                      className="flex-1 bg-yellow-500 text-white py-2 px-3 rounded-[2rem] hover:bg-yellow-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-1 text-sm"
                    >
                      {loading ? (
                        <FaSpinner className="w-3 h-3 animate-spin" />
                      ) : (
                        <>
                          <FaTrophy className="w-3 h-3" />
                          <span>Update Winners</span>
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowWinnerModal(false);
                        resetWinnerForm();
                      }}
                      className="px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Create Live Session Modal - Ultra Compact */}
      <AnimatePresence>
        {showSessionModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowSessionModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4">
                {/* Header */}
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    <FaVideo className="w-4 h-4 text-red-500" />
                    Create Live Session
                  </h2>
                  <button
                    onClick={() => setShowSessionModal(false)}
                    className="text-gray-500 hover:text-gray-700 p-1 rounded-lg hover:bg-gray-100"
                  >
                    <FaTimes className="w-4 h-4" />
                  </button>
                </div>

                <form onSubmit={handleCreateSession} className="space-y-3">
                  {/* Row 1: Title and Event */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Session Title *</label>
                      <input
                        type="text"
                        value={sessionForm.title}
                        onChange={(e) => setSessionForm({...sessionForm, title: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-red-500 text-sm"
                        placeholder="Cultural Dance Competition - Round 1"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Associated Event *</label>
                      <select
                        value={sessionForm.eventId}
                        onChange={(e) => setSessionForm({...sessionForm, eventId: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-red-500 text-sm"
                        required
                      >
                        <option value="">Select event...</option>
                        {events.map((event) => (
                          <option key={event._id} value={event._id}>{event.title}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Row 2: Category, Participants, Rounds, Time */}
                  <div className="grid grid-cols-4 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Category</label>
                      <select
                        value={sessionForm.category}
                        onChange={(e) => setSessionForm({...sessionForm, category: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-red-500 text-sm"
                      >
                        <option value="cultural">Cultural</option>
                        <option value="modern">Modern</option>
                        <option value="traditional">Traditional</option>
                        <option value="freestyle">Freestyle</option>
                        <option value="group">Group</option>
                        <option value="solo">Solo</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Max Users</label>
                      <input
                        type="number"
                        value={sessionForm.maxParticipants}
                        onChange={(e) => setSessionForm({...sessionForm, maxParticipants: parseInt(e.target.value)})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-red-500 text-sm"
                        min="1" max="50" required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Rounds</label>
                      <input
                        type="number"
                        value={sessionForm.roundsCount}
                        onChange={(e) => setSessionForm({...sessionForm, roundsCount: parseInt(e.target.value)})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-red-500 text-sm"
                        min="1" max="10" required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Time/Round</label>
                      <input
                        type="number"
                        value={sessionForm.timePerRound}
                        onChange={(e) => setSessionForm({...sessionForm, timePerRound: parseInt(e.target.value)})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-red-500 text-sm"
                        min="1" max="60" required
                      />
                    </div>
                  </div>

                  {/* Row 3: Description and Stream Settings */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="col-span-2">
                      <label className="block text-xs font-medium text-gray-700 mb-1">Description *</label>
                      <textarea
                        value={sessionForm.description}
                        onChange={(e) => setSessionForm({...sessionForm, description: e.target.value})}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-red-500 text-sm resize-none"
                        placeholder="Competition format, rules, or special instructions..."
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Stream URL</label>
                        <input
                          type="url"
                          value={sessionForm.streamUrl}
                          onChange={(e) => setSessionForm({...sessionForm, streamUrl: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-red-500 text-sm"
                          placeholder="https://youtube.com/live/..."
                        />
                      </div>
                      <label className="flex items-center gap-2 pt-2">
                        <input
                          type="checkbox"
                          checked={sessionForm.isLive}
                          onChange={(e) => setSessionForm({...sessionForm, isLive: e.target.checked})}
                          className="w-4 h-4 text-red-600 rounded border-gray-300"
                        />
                        <span className="text-xs text-gray-700">Start immediately</span>
                      </label>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex space-x-2 pt-3">
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 bg-red-500 text-white py-2 px-3 rounded-[2rem] hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center justify-center space-x-1 text-sm"
                    >
                      {loading ? (
                        <FaSpinner className="w-3 h-3 animate-spin" />
                      ) : (
                        <>
                          <FaVideo className="w-3 h-3" />
                          <span>Create Session</span>
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowSessionModal(false)}
                      className="px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Update Score Modal - Ultra Compact */}
      <AnimatePresence>
        {showScoreModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowScoreModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl max-w-2xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                {/* Header */}
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <FaStar className="w-5 h-5 text-yellow-500" />
                    Update Participant Score
                  </h2>
                  <button
                    onClick={() => setShowScoreModal(false)}
                    className="text-gray-500 hover:text-gray-700 p-1 rounded-lg hover:bg-gray-100"
                  >
                    <FaTimes className="w-5 h-5" />
                  </button>
                </div>

                <form onSubmit={handleUpdateScore} className="space-y-4">
                  {/* Session and Participant */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Live Session *</label>
                      <select
                        value={scoreForm.sessionId}
                        onChange={(e) => setScoreForm({...scoreForm, sessionId: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-yellow-500"
                        required
                      >
                        <option value="">Select session...</option>
                        {liveSessions.map((session) => (
                          <option key={session._id} value={session._id}>{session.title}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Participant *</label>
                      <select
                        value={scoreForm.userId}
                        onChange={(e) => setScoreForm({...scoreForm, userId: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-yellow-500"
                        required
                      >
                        <option value="">Select participant...</option>
                        {users.map((user) => (
                          <option key={user._id} value={user._id}>
                            {user.name || user.teamName || user.schoolName}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Scoring Grid */}
                  <div className="bg-gray-50 rounded-xl p-4">
                    <h3 className="text-sm font-semibold text-gray-900 mb-4">Scoring Criteria (0-100 each)</h3>
                    
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Technique *</label>
                        <input
                          type="number"
                          value={scoreForm.technique}
                          onChange={(e) => {
                            const newTechnique = parseInt(e.target.value) || 0;
                            const newOverall = Math.round((newTechnique + scoreForm.composition + scoreForm.creativity) / 3);
                            setScoreForm({
                              ...scoreForm, 
                              technique: newTechnique,
                              overall: newOverall
                            });
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-yellow-500"
                          min="0" max="100" required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Composition *</label>
                        <input
                          type="number"
                          value={scoreForm.composition}
                          onChange={(e) => {
                            const newComposition = parseInt(e.target.value) || 0;
                            const newOverall = Math.round((scoreForm.technique + newComposition + scoreForm.creativity) / 3);
                            setScoreForm({
                              ...scoreForm, 
                              composition: newComposition,
                              overall: newOverall
                            });
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-yellow-500"
                          min="0" max="100" required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Creativity *</label>
                        <input
                          type="number"
                          value={scoreForm.creativity}
                          onChange={(e) => {
                            const newCreativity = parseInt(e.target.value) || 0;
                            const newOverall = Math.round((scoreForm.technique + scoreForm.composition + newCreativity) / 3);
                            setScoreForm({
                              ...scoreForm, 
                              creativity: newCreativity,
                              overall: newOverall
                            });
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-yellow-500"
                          min="0" max="100" required
                        />
                      </div>
                    </div>

                    {/* Auto-calculated Overall Score */}
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 text-center">
                      <div className="text-sm font-medium text-blue-700 mb-1">Overall Score (Auto-calculated)</div>
                      <div className="text-2xl font-bold text-blue-600">
                        {scoreForm.overall}/100
                      </div>
                      <div className="text-lg font-semibold text-green-600 mt-1">
                        Total: {scoreForm.technique + scoreForm.composition + scoreForm.creativity + scoreForm.overall}/400
                      </div>
                    </div>
                  </div>

                  {/* Feedback */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Judge Comments (Optional)</label>
                    <textarea
                      value={scoreForm.remarks}
                      onChange={(e) => setScoreForm({...scoreForm, remarks: e.target.value})}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-yellow-500 resize-none"
                      placeholder="Comments for the participant..."
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="flex space-x-3 pt-4">
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 bg-green-500 text-white py-3 px-4 rounded-xl hover:bg-green-600 transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
                    >
                      {loading ? (
                        <FaSpinner className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <FaSave className="w-4 h-4" />
                          <span>Update Score</span>
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowScoreModal(false)}
                      className="px-4 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Create/Edit Program Modal - Ultra Compact */}
      <AnimatePresence>
        {showProgramModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowProgramModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl max-w-4xl w-full max-h-[92vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4">
                {/* Header */}
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    <FaGraduationCap className="w-4 h-4 text-green-500" />
                    {editingProgram ? "Edit Program" : "Create Program"}
                  </h2>
                  <button
                    onClick={() => setShowProgramModal(false)}
                    className="text-gray-500 hover:text-gray-700 p-1 rounded-lg hover:bg-gray-100"
                  >
                    <FaTimes className="w-4 h-4" />
                  </button>
                </div>

                <form onSubmit={handleCreateProgram} className="space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    {/* Left Column */}
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Program Title *</label>
                        <input
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-green-500 text-sm"
                          value={programForm.title}
                          onChange={e => setProgramForm({...programForm, title: e.target.value})}
                          placeholder="Advanced Music & Arts Program"
                          required
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Duration *</label>
                          <input
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-green-500 text-sm"
                            value={programForm.duration}
                            onChange={e => setProgramForm({...programForm, duration: e.target.value})}
                            placeholder="6 months"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Price (KES)</label>
                          <input
                            type="number"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-green-500 text-sm"
                            value={programForm.price.amount}
                            onChange={e => setProgramForm({...programForm, price: {...programForm.price, amount: +e.target.value || 0}})}
                            placeholder="15000"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Icon</label>
                          <select
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-green-500 text-sm"
                            value={programForm.icon}
                            onChange={e => setProgramForm({...programForm, icon: e.target.value})}
                          >
                            <option value="FaUsers">👥 Users</option>
                            <option value="FaMicrophone">🎤 Microphone</option>
                            <option value="FaBookOpen">📖 Book</option>
                            <option value="FaPalette">🎨 Palette</option>
                            <option value="FaFootballBall">⚽ Sports</option>
                            <option value="FaCrown">👑 Crown</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Category</label>
                          <select
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-green-500 text-sm"
                            value={programForm.category}
                            onChange={e => setProgramForm({...programForm, category: e.target.value})}
                          >
                            <option value="core">Core</option>
                            <option value="academic">Academic</option>
                            <option value="cultural">Cultural</option>
                            <option value="creative">Creative</option>
                            <option value="premium">Premium</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Capacity *</label>
                          <input
                            type="number"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-green-500 text-sm"
                            value={programForm.capacity}
                            onChange={e => setProgramForm({...programForm, capacity: +e.target.value})}
                            placeholder="25"
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Lead Instructor</label>
                        <input
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-green-500 text-sm"
                          value={programForm.instructor.name}
                          onChange={e => setProgramForm({...programForm, instructor: {...programForm.instructor, name: e.target.value}})}
                          placeholder="Dr. Sarah Wilson"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Min Age</label>
                          <input
                            type="number"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-green-500 text-sm"
                            value={programForm.targetAge.min}
                            onChange={e => setProgramForm({...programForm, targetAge: {...programForm.targetAge, min: +e.target.value || 5}})}
                            placeholder="8"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Max Age</label>
                          <input
                            type="number"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-green-500 text-sm"
                            value={programForm.targetAge.max}
                            onChange={e => setProgramForm({...programForm, targetAge: {...programForm.targetAge, max: +e.target.value || 18}})}
                            placeholder="16"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Right Column */}
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Program Description *</label>
                        <textarea
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-green-500 text-sm resize-none"
                          value={programForm.description}
                          onChange={e => setProgramForm({...programForm, description: e.target.value})}
                          placeholder="Comprehensive program covering music theory, performance, and creative expression..."
                          required
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Badge/Certificate *</label>
                          <input
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-green-500 text-sm"
                            value={programForm.badge}
                            onChange={e => setProgramForm({...programForm, badge: e.target.value})}
                            placeholder="Music Excellence Certificate"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Enrollment Status</label>
                          <select
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-green-500 text-sm"
                            value={programForm.enrollmentStatus}
                            onChange={e => setProgramForm({...programForm, enrollmentStatus: e.target.value})}
                          >
                            <option value="open">Open for Enrollment</option>
                            <option value="closed">Enrollment Closed</option>
                            <option value="waitlist">Waitlist Only</option>
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Instructor Biography</label>
                        <textarea
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-green-500 text-sm resize-none"
                          value={programForm.instructor.bio}
                          onChange={e => setProgramForm({...programForm, instructor: {...programForm.instructor, bio: e.target.value}})}
                          placeholder="Experienced educator with 15+ years in music instruction..."
                        />
                      </div>

                      <div className="flex gap-4 items-center">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={programForm.isFeatured}
                            onChange={e => setProgramForm({...programForm, isFeatured: e.target.checked})}
                            className="w-4 h-4 text-green-600 rounded border-gray-300"
                          />
                          <span className="text-xs text-gray-700">Featured Program</span>
                        </label>
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={programForm.isActive}
                            onChange={e => setProgramForm({...programForm, isActive: e.target.checked})}
                            className="w-4 h-4 text-green-600 rounded border-gray-300"
                          />
                          <span className="text-xs text-gray-700">Active Program</span>
                        </label>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Program Thumbnail</label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={e => setProgramForm({...programForm, thumbnailFile: e.target.files[0]})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-green-500 text-sm"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Features Section */}
                  <div className="border-t border-gray-100 pt-3">
                    <label className="block text-xs font-medium text-gray-700 mb-2">Program Features</label>
                    <div className="grid grid-cols-2 gap-2 max-h-20 overflow-y-auto">
                      {programForm.features.map((feature, index) => (
                        <div key={index} className="flex gap-2">
                          <input
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-green-500 text-sm"
                            value={feature}
                            onChange={e => updateArrayField("features", index, e.target.value)}
                            placeholder={`Feature ${index + 1}`}
                          />
                          {programForm.features.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeArrayField("features", index)}
                              className="text-red-500 hover:text-red-700 px-2"
                            >
                              <FaTimes className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={() => addArrayField("features")}
                      className="text-xs text-green-600 hover:text-green-800 mt-2 flex items-center gap-1"
                    >
                      <FaPlus className="w-3 h-3" />
                      Add Feature
                    </button>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex space-x-2 pt-3">
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 bg-green-500 text-white py-2 px-3 rounded-[2rem] hover:bg-green-600 transition-colors disabled:opacity-50 flex items-center justify-center space-x-1 text-sm"
                    >
                      {loading ? (
                        <FaSpinner className="w-3 h-3 animate-spin" />
                      ) : (
                        <>
                          <FaSave className="w-3 h-3" />
                          <span>{editingProgram ? "Update Program" : "Create Program"}</span>
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowProgramModal(false);
                        setEditingProgram(null);
                        resetProgramForm();
                      }}
                      className="px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>}


      <AdvancedAnalytics3D 
        isOpen={showAdvancedAnalytics}
        onClose={() => setShowAdvancedAnalytics(false)}
        data={{
          users,
          events,
          liveSessions,
          winners,
          notifications
        }}
      />
    </div>
  );
};

export default SuperAdminDashboard;
